import React, { useState, useEffect, useMemo, useRef } from 'react';
import config from './config';
import { MathJaxContext, MathJax } from 'better-react-mathjax';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getOrganizationHeaders, withOrganizationSlug } from './orgContext';
import { fetchCoursesCached } from './courseApi';
import DynamicComponentRenderer from './components/dynamic/DynamicComponentRenderer';
import { CategoryDynamicComponent } from './types/dynamicComponents';
import { enhanceQuestionSvg } from './utils/svgEnhancers';

interface Category {
  id: number;
  name: string;
  description: string;
  sortOrder?: number;
  includeInTasks?: boolean;
  detailedDescription?: string;
  tips?: string;
  example?: string;
  parentCategoryId: number | null;
  accessibilityType?: number; // 1=Free, 2=Bronze, 3=Silver, 4=Gold
  isEnabled?: boolean; // Whether the category is enabled/active
  categoryDiagrams?: CategoryDiagram[];
  dynamicComponents?: CategoryDynamicComponent[];
  subCategories: Category[];
}

interface CategoryDiagram {
  id: number;
  diagramId: number;
  imageUrl?: string;
  placementType: number;
  displaySize?: number;
  displayOrder: number;
  introHtml?: string;
  captionOverride?: string;
  diagramType: string;
  defaultCaption?: string;
}

const getDiagramImageBaseUrl = (): string => {
  try {
    const apiUrl = new URL(config.apiBaseUrl);
    const normalizedPath = apiUrl.pathname.replace(/\/+$/, '').replace(/\/api$/i, '');
    return `${apiUrl.origin}${normalizedPath ? `${normalizedPath}/` : '/'}`;
  } catch {
    return `${config.apiBaseUrl.replace(/\/+$/, '').replace(/\/api$/i, '')}/`;
  }
};

const DIAGRAM_IMAGE_BASE_URL = getDiagramImageBaseUrl();

const resolveDiagramImageUrl = (imageUrl: string): string => {
  const trimmedUrl = imageUrl.trim();

  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return trimmedUrl;
  }

  const normalizedPath = trimmedUrl.startsWith('/') ? trimmedUrl.substring(1) : trimmedUrl;
  return `${DIAGRAM_IMAGE_BASE_URL}${normalizedPath}`;
};

const getDiagramWidthBySize = (displaySize?: number): string => {
  switch (displaySize) {
    case 1:
      return '60%';
    case 3:
      return '90%';
    case 4:
      return '100%';
    case 2:
    default:
      return '75%';
  }
};

interface Question {
  id: number;
  categoryId: number;
  questionText: string;
  answerText: string;
  explanation: string;
  hint?: string;
  difficultyLevel: number;
  isActive: boolean;
  source: string;
  imageUrl: string;
  optionA?: string | null;
  optionB?: string | null;
  optionC?: string | null;
  optionD?: string | null;
  optionE?: string | null;
  correctOption?: string | null;
  isMultipleChoice?: boolean;
  questionLatex?: string;
  answerLatex?: string;
  questionForOpenAI?: string;
  createdAt: string;
  updatedAt: string;
}

interface AiExplanationStep {
  stepNumber?: number;
  explanation?: string;
  equationLatex?: string;
}

interface AiExplanationPayload {
  title?: string;
  steps?: AiExplanationStep[];
  finalAnswer?: string;
  checks?: string[];
  tips?: string[];
}

const checkCategoryExists = (cats: Category[], categoryId: number): boolean => {
  for (const cat of cats) {
    if (cat.id === categoryId) return true;
    if (cat.subCategories && cat.subCategories.length > 0) {
      if (checkCategoryExists(cat.subCategories, categoryId)) return true;
    }
  }
  return false;
};

const getCategorySortOrder = (category: Category): number => {
  if (typeof category.sortOrder === 'number' && category.sortOrder > 0) {
    return category.sortOrder;
  }

  return Number.MAX_SAFE_INTEGER;
};

const sortCategoriesByLevelAndOrder = (items: Category[]): Category[] => {
  return [...items]
    .sort((first, second) => {
      const firstOrder = getCategorySortOrder(first);
      const secondOrder = getCategorySortOrder(second);

      if (firstOrder !== secondOrder) {
        return firstOrder - secondOrder;
      }

      return first.name.localeCompare(second.name);
    })
    .map((item) => ({
      ...item,
      subCategories: item.subCategories ? sortCategoriesByLevelAndOrder(item.subCategories) : item.subCategories,
    }));
};

// Drawing Canvas Component
const DrawingCanvas: React.FC<{ canvasRef: React.RefObject<HTMLCanvasElement | null>, onQuestionChange: () => void }> = ({ canvasRef, onQuestionChange }) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [penColor, setPenColor] = useState('#000000');
  const [penSize, setPenSize] = useState(2);
  const [tool, setTool] = useState<'pen' | 'eraser'>('pen');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = canvas.offsetWidth;
    canvas.height = window.innerWidth <= 480 ? 250 : window.innerWidth <= 768 ? 300 : 400;

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Border
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, canvas.width, canvas.height);
  }, []);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : (e as React.MouseEvent).clientX - rect.left;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : (e as React.MouseEvent).clientY - rect.top;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : (e as React.MouseEvent).clientX - rect.left;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : (e as React.MouseEvent).clientY - rect.top;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (tool === 'eraser') {
      ctx.clearRect(x - penSize / 2, y - penSize / 2, penSize, penSize);
    } else {
      ctx.strokeStyle = penColor;
      ctx.lineWidth = penSize;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Border
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, canvas.width, canvas.height);
  };

  return (
    <div style={{
      marginBottom: 20,
      borderRadius: 6,
      border: '2px solid #ff9800',
      backgroundColor: '#fff',
      overflow: 'hidden'
    }}>
      <div style={{
        backgroundColor: '#fff8f0',
        padding: window.innerWidth <= 480 ? 8 : 12,
        borderBottom: '1px solid #ff9800',
        fontSize: window.innerWidth <= 480 ? 11 : 12,
        color: '#666'
      }}>
        📝 Write down your steps here. Use the pen to draw and eraser to correct.
      </div>
      
      <div style={{
        padding: 12,
        backgroundColor: '#fafafa',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        gap: window.innerWidth <= 480 ? 8 : 12,
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => setTool('pen')}
          style={{
            padding: window.innerWidth <= 480 ? '5px 10px' : '6px 12px',
            backgroundColor: tool === 'pen' ? '#ff9800' : '#e0e0e0',
            color: tool === 'pen' ? '#fff' : '#333',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: window.innerWidth <= 480 ? 11 : 12,
            fontWeight: 600
          }}
        >
          ✏️ Pen
        </button>
        
        <button
          onClick={() => setTool('eraser')}
          style={{
            padding: window.innerWidth <= 480 ? '5px 10px' : '6px 12px',
            backgroundColor: tool === 'eraser' ? '#ff9800' : '#e0e0e0',
            color: tool === 'eraser' ? '#fff' : '#333',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: window.innerWidth <= 480 ? 11 : 12,
            fontWeight: 600
          }}
        >
          🧹 Eraser
        </button>

        <input
          type="color"
          value={penColor}
          onChange={(e) => setPenColor(e.target.value)}
          disabled={tool === 'eraser'}
          style={{
            width: window.innerWidth <= 480 ? 32 : 40,
            height: window.innerWidth <= 480 ? 28 : 32,
            border: '1px solid #ccc',
            borderRadius: 4,
            cursor: tool === 'eraser' ? 'not-allowed' : 'pointer'
          }}
        />

        <select
          value={penSize}
          onChange={(e) => setPenSize(Number(e.target.value))}
          style={{
            padding: window.innerWidth <= 480 ? '5px 6px' : '6px 8px',
            borderRadius: 4,
            border: '1px solid #ccc',
            fontSize: window.innerWidth <= 480 ? 11 : 12
          }}
        >
          <option value={1}>Thin</option>
          <option value={2}>Normal</option>
          <option value={4}>Medium</option>
          <option value={6}>Thick</option>
        </select>

        <button
          onClick={clearCanvas}
          style={{
            padding: window.innerWidth <= 480 ? '5px 10px' : '6px 12px',
            backgroundColor: '#f44336',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: window.innerWidth <= 480 ? 11 : 12,
            fontWeight: 600,
            marginLeft: window.innerWidth <= 480 ? '0' : 'auto'
          }}
        >
          🗑️ Clear
        </button>
      </div>

      <canvas
        ref={canvasRef}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
        style={{
          display: 'block',
          width: '100%',
          height: window.innerWidth <= 480 ? 250 : window.innerWidth <= 768 ? 300 : 400,
          cursor: tool === 'pen' ? 'crosshair' : 'default',
          touchAction: 'none'
        }}
      />
    </div>
  );
};

const PracticeTasks: React.FC = () => {
  const { user, isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const courseIdFromURL = searchParams.get('courseId');
  const selectedCategoryIdFromURL = searchParams.get('categoryId');
  
  const [courses, setCourses] = useState<any[]>([]);
  const [enrolledCourses, setEnrolledCourses] = useState<any[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [loading, setLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userAnswers, setUserAnswers] = useState<{ [key: number]: string }>({});
  const [submittedAnswers, setSubmittedAnswers] = useState<Set<number>>(new Set());
  const [answerResults, setAnswerResults] = useState<{ [key: number]: boolean }>({});
  const [answerErrors, setAnswerErrors] = useState<{ [key: number]: string }>({});
  const [aiExplanation, setAiExplanation] = useState<string | null>(null);
  const [aiExplanationJson, setAiExplanationJson] = useState<AiExplanationPayload | null>(null);
  const [aiExplanationSource, setAiExplanationSource] = useState<'json' | 'text' | null>(null);
  const [loadingExplanation, setLoadingExplanation] = useState(false);
  const [explanationError, setExplanationError] = useState<string | null>(null);
  const [isDrawingPanelOpen, setIsDrawingPanelOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showQuestionDetails, setShowQuestionDetails] = useState(false);
  const [showCategoryHelp, setShowCategoryHelp] = useState(false);
  const [showHintInline, setShowHintInline] = useState(false);
  const [showReportPanel, setShowReportPanel] = useState(false);
  const [reportIssueType, setReportIssueType] = useState('Wrong answer');
  const [reportDetails, setReportDetails] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const [expandedDetailDescription, setExpandedDetailDescription] = useState(false);
  const [expandedExample, setExpandedExample] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const [showDetailedDescInQuestion, setShowDetailedDescInQuestion] = useState(true);
  const [showExampleInQuestion, setShowExampleInQuestion] = useState(false);
  const [userSubscriptionTier, setUserSubscriptionTier] = useState<number>(1);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState<string | null>(null);
  const [lastAttemptStatus, setLastAttemptStatus] = useState<{[key: number]: {isCorrect: boolean, attemptedAt: string} | null}>({});
  const [showTopicsMenu, setShowTopicsMenu] = useState(() => !courseIdFromURL);
  const [categorySearch, setCategorySearch] = useState('');
  const [dynamicComponentSelections, setDynamicComponentSelections] = useState<Record<number, number>>({});
  const excalidrawRef = useRef<HTMLCanvasElement>(null);
  const expandedCategoriesInitialized = useRef(false);
  const answerInputRef = useRef<HTMLTextAreaElement>(null);
  const questionHtmlRef = useRef<HTMLDivElement>(null);

  const breadcrumbCourseName = useMemo(() => {
    if (!courseIdFromURL) {
      return null;
    }

    const numericCourseId = Number(courseIdFromURL);
    const isNumeric = Number.isFinite(numericCourseId) && numericCourseId > 0;

    const courseFromList = isNumeric
      ? courses.find((course: any) => Number(course.id) === numericCourseId)?.name
      : courses.find((course: any) => course.inforPagenav === courseIdFromURL)?.name;
    if (courseFromList) {
      return courseFromList;
    }

    if (isNumeric) {
      const courseFromEnrollment = enrolledCourses.find((enrollment: any) => Number(enrollment.courseId) === numericCourseId)?.courseName;
      return courseFromEnrollment ?? null;
    }

    return null;
  }, [courseIdFromURL, courses, enrolledCourses]);

  const isMobile = windowWidth <= 480;
  const isTablet = windowWidth <= 768;
  const isSmallScreen = isMobile || isTablet;
  const selectedQuestionId = selectedQuestion?.id;
  const selectedQuestionText = selectedQuestion?.questionText;
  const publicOrgSlug = (config.publicOrgSlug || 'em').trim().toLowerCase();
  const guestAwarePath = (path: string) => (isAuthenticated ? withOrganizationSlug(path) : `/${publicOrgSlug}${path}`);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!courseIdFromURL) {
      setShowTopicsMenu(true);
    }
  }, [courseIdFromURL]);

  useEffect(() => {
    if (!selectedCategory) {
      setShowTopicsMenu(true);
    }
  }, [selectedCategory]);

  useEffect(() => {
    setAiExplanation(null);
    setAiExplanationJson(null);
    setAiExplanationSource(null);
    setExplanationError(null);
    setLoadingExplanation(false);
    setShowCategoryHelp(false);
    setShowHintInline(false);
    setShowReportPanel(false);
    setReportIssueType('Wrong answer');
    setReportDetails('');
    setReportSubmitted(false);
  }, [selectedQuestion?.id]);

  useEffect(() => {
    if (!selectedQuestion) {
      return;
    }

    if (isSmallScreen && !showQuestionDetails) {
      return;
    }

    const focusTimeout = window.setTimeout(() => {
      answerInputRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(focusTimeout);
  }, [selectedQuestion?.id, showQuestionDetails, isSmallScreen]);

  useEffect(() => {
    if (!selectedQuestionId || !selectedQuestionText || !questionHtmlRef.current || showCategoryHelp) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      enhanceQuestionSvg(questionHtmlRef.current as ParentNode);
    });

    return () => window.cancelAnimationFrame(frame);
  });

  useEffect(() => {
    const fetchCourseNameForBreadcrumb = async () => {
      if (!courseIdFromURL || breadcrumbCourseName) {
        return;
      }

      try {
        const coursesData = await fetchCoursesCached();
        setCourses(Array.isArray(coursesData) ? coursesData : []);
      } catch (error) {
        console.error('Error fetching courses for breadcrumb:', error);
      }
    };

    fetchCourseNameForBreadcrumb();
  }, [courseIdFromURL, breadcrumbCourseName]);

  // Strip HTML/SVG tags and return plain text for list previews
  const stripHtml = (html: string): string => {
    if (!html) return html;
    // Remove <svg>...</svg> blocks entirely
    let text = html.replace(/<svg[\s\S]*?<\/svg>/gi, '');
    // Remove remaining HTML tags
    text = text.replace(/<[^>]+>/g, ' ');
    // Collapse whitespace
    return text.replace(/\s+/g, ' ').trim();
  };

  // Helper function to convert LaTeX text commands to HTML
  const convertLatexToHtml = (text: string): string => {
    if (!text) return text;
    
    // Convert \textbf{...} to <strong>...</strong>
    text = text.replace(/\\textbf\{([^}]*)\}/g, '<strong>$1</strong>');
    
    // Convert \textit{...} to <em>...</em>
    text = text.replace(/\\textit\{([^}]*)\}/g, '<em>$1</em>');
    
    // Convert \underline{...} to <u>...</u>
    text = text.replace(/\\underline\{([^}]*)\}/g, '<u>$1</u>');
    
    // Convert \texttt{...} to <code>...</code>
    text = text.replace(/\\texttt\{([^}]*)\}/g, '<code>$1</code>');
    
    // Convert \text{...} to just the text content
    text = text.replace(/\\text\{([^}]*)\}/g, '$1');
    
    return text;
  };

  const containsLatexLikeMath = (text: string): boolean => {
    return /\\[a-zA-Z]+|[A-Za-z0-9]\^\{[^}]+\}|[A-Za-z0-9]_\{[^}]+\}/.test(text);
  };

  const formatInlineLatexForMathJax = (text: string): string => {
    if (!text) return text;

    // Respect already-delimited math.
    if (/\\\(|\\\[|\$\$?|\\begin\{/.test(text)) {
      return text;
    }

    if (!containsLatexLikeMath(text)) {
      return text;
    }

    const colonIndex = text.indexOf(':');
    if (colonIndex >= 0) {
      const prefix = text.slice(0, colonIndex + 1);
      const suffix = text.slice(colonIndex + 1).trim();

      if (containsLatexLikeMath(suffix)) {
        return `${prefix} \\(${suffix}\\)`;
      }
    }

    // If the whole line appears to be mathematical content, wrap all of it.
    return `\\(${text}\\)`;
  };

  // Helper function to normalize mathematical expressions
  const normalizeMathExpression = (expr: string): string => {
    // Remove spaces
    let normalized = expr.replace(/\s+/g, '');
    
    // Convert LaTeX fractions to plain text
    // \frac{a}{b} -> a/b
    normalized = normalized.replace(/\\frac\{([^}]*)\}\{([^}]*)\}/g, '$1/$2');
    
    // Remove other LaTeX commands
    normalized = normalized.replace(/\\/g, '');
    
    // Normalize case for variables
    normalized = normalized.toLowerCase();
    
    return normalized;
  };

  // Helper function to check if answer is correct (case-insensitive, handles LaTeX)
  const isAnswerCorrect = (userAnswer: string, correctAnswer: string): boolean => {
    // Normalize both answers
    const normalizedUser = normalizeMathExpression(userAnswer);
    const normalizedCorrect = normalizeMathExpression(correctAnswer);
    
    // Check if user provided an answer
    if (!normalizedUser) {
      return false;
    }
    
    // Check for exact match or if correct is contained in user answer (for partial credit scenarios)
    return normalizedUser === normalizedCorrect || normalizedCorrect === normalizedUser;
  };

  const prepareForMathJax = (value?: string | null): string => {
    const text = (value || '').trim();
    if (!text) return '';

    const hasDelimiters = /\$|\\\(|\\\[/.test(text);
    const looksLikeLatex = /\\[a-zA-Z]+|\^|_/.test(text);

    if (!hasDelimiters && looksLikeLatex) {
      return `\\(${text}\\)`;
    }

    return text;
  };

  const toHtmlMarkup = (value?: string | null): string => {
    const prepared = prepareForMathJax(value);
    if (!prepared) return '';

    const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(prepared);
    if (hasHtmlTags) {
      return prepared;
    }

    const escaped = prepared
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    return escaped.replace(/\r?\n/g, '<br/>');
  };

  const getQuestionOptionEntries = (question: Question) => {
    return [
      { key: 'A', value: question.optionA },
      { key: 'B', value: question.optionB },
      { key: 'C', value: question.optionC },
      { key: 'D', value: question.optionD },
      { key: 'E', value: question.optionE },
    ].filter(option => !!option.value && option.value.trim().length > 0);
  };

  const getCorrectAnswerForQuestion = (question: Question) => {
    if (question.isMultipleChoice && question.correctOption) {
      const selected = getQuestionOptionEntries(question)
        .find(option => option.key === question.correctOption?.trim().toUpperCase());
      return selected?.value || '';
    }

    return question.answerText || question.answerLatex || '';
  };

  const renderDiagramsByPlacement = (selectedCat: Category | undefined, placementType: number) => {
    if (!selectedCat?.categoryDiagrams || selectedCat.categoryDiagrams.length === 0) {
      return null;
    }

    const diagramsForPlacement = selectedCat.categoryDiagrams
      .filter((diagram) => diagram.placementType === placementType)
      .sort((a, b) => a.displayOrder - b.displayOrder);

    if (diagramsForPlacement.length === 0) {
      return null;
    }

    return (
      <div style={{ marginBottom: 16 }}>
        {diagramsForPlacement.map((diagram) => {
          const introHtmlValue = diagram.introHtml?.trim();
          const captionOverrideValue = diagram.captionOverride?.trim();
          const defaultCaptionValue = diagram.defaultCaption?.trim();
          const imageUrlValue = diagram.imageUrl?.trim();
          const diagramSource = imageUrlValue
            ? resolveDiagramImageUrl(imageUrlValue)
            : `${config.apiBaseUrl}/api/diagrams/${diagram.diagramId}/svg`;

          return (
            <div
              key={diagram.id}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                backgroundColor: '#ffffff',
                padding: 12,
                marginBottom: 12,
              }}
            >
              {introHtmlValue && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Intro</div>
                  <div
                    className="html-content"
                    style={{ fontSize: 13, lineHeight: 1.6, textAlign: 'left' }}
                    dangerouslySetInnerHTML={{ __html: introHtmlValue }}
                  />
                </div>
              )}

              <div
                style={{
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  backgroundColor: '#fff',
                  padding: 8,
                }}
              >
                <img
                  src={diagramSource}
                  alt={`${diagram.diagramType} diagram`}
                  style={{ width: getDiagramWidthBySize(diagram.displaySize), maxWidth: '100%', height: 'auto', display: 'block', margin: '0 auto' }}
                />
              </div>

              {captionOverrideValue && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4, textAlign: 'center' }}>
                    Caption Override
                  </div>
                  <p style={{ margin: 0, color: '#4b5563', fontSize: 12, textAlign: 'center' }}>
                    {captionOverrideValue}
                  </p>
                </div>
              )}

              {!captionOverrideValue && defaultCaptionValue && (
                <p style={{ marginTop: 8, marginBottom: 0, color: '#4b5563', fontSize: 12, textAlign: 'center' }}>
                  {defaultCaptionValue}
                </p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const handleDynamicComponentSelection = (componentId: number, value: number) => {
    setDynamicComponentSelections((previous) => ({
      ...previous,
      [componentId]: value,
    }));
  };

  const renderDynamicComponentsByPlacement = (selectedCat: Category | undefined, placementType: number) => {
    return (
      <div style={{ marginBottom: 16 }}>
        <DynamicComponentRenderer
          components={selectedCat?.dynamicComponents}
          placementType={placementType}
          selections={dynamicComponentSelections}
          onSelectionChange={handleDynamicComponentSelection}
        />
      </div>
    );
  };

  // Fetch top-level categories on mount
  useEffect(() => {
    const expandAllCategories = (cats: Category[]): Set<number> => {
      const ids = new Set<number>();
      const addIds = (categoriesToExpand: Category[], depth: number) => {
        categoriesToExpand.forEach(cat => {
          if (depth === 0 && cat.subCategories && cat.subCategories.length > 0) {
            ids.add(cat.id);
          }
          if (cat.subCategories && cat.subCategories.length > 0) {
            addIds(cat.subCategories, depth + 1);
          }
        });
      };
      addIds(cats, 0);
      return ids;
    };

    const fetchCategories = async () => {
      try {
        const applySelectedCategoryFromUrl = (categoryData: Category[]) => {
          const parsedCategoryId = selectedCategoryIdFromURL ? parseInt(selectedCategoryIdFromURL, 10) : null;
          if (!parsedCategoryId || !checkCategoryExists(categoryData, parsedCategoryId)) {
            return;
          }

          setSelectedCategory(parsedCategoryId);
          setAccessDeniedMessage(null);
          if (window.innerWidth <= 768) {
            setShowTopicsMenu(false);
          }
        };

        // If no courseId, fetch available courses
        if (!courseIdFromURL) {
          setCategoryLoading(true);
          const coursesData = await fetchCoursesCached();
          setCourses(Array.isArray(coursesData) ? coursesData : []);

          // Fetch enrolled courses if authenticated
          if (isAuthenticated) {
            try {
              const token = await getAccessTokenSilently();
              const enrollmentsUrl = `${config.apiBaseUrl}/api/courses/user/enrollments`;
              const enrollmentsResponse = await fetch(enrollmentsUrl, {
                headers: {
                  Authorization: `Bearer ${token}`,
                  ...getOrganizationHeaders(),
                }
              });
              if (enrollmentsResponse.ok) {
                const enrollmentsData = await enrollmentsResponse.json();
                setEnrolledCourses(enrollmentsData);
              }
            } catch (err) {
              console.error('Error fetching enrollments:', err);
            }
          }
          setCategories([]);
          setError(null);
          setCategoryLoading(false);
          return;
        }

        setCategoryLoading(true);
        expandedCategoriesInitialized.current = false;
        const categoryCacheKey = `practice-categories-${courseIdFromURL}`;

        const cachedCategories = sessionStorage.getItem(categoryCacheKey);
        if (cachedCategories) {
          try {
            const parsedCached = JSON.parse(cachedCategories) as Category[];
            if (Array.isArray(parsedCached) && parsedCached.length > 0) {
              const sortedCachedCategories = sortCategoriesByLevelAndOrder(parsedCached);
              setCategories(sortedCachedCategories);
              if (!expandedCategoriesInitialized.current) {
                setExpandedCategories(expandAllCategories(sortedCachedCategories));
                expandedCategoriesInitialized.current = true;
              }
              applySelectedCategoryFromUrl(sortedCachedCategories);
            }
          } catch {
            sessionStorage.removeItem(categoryCacheKey);
          }
        }

        const url = `${config.apiBaseUrl}/api/categories/by-course/${courseIdFromURL}?includeInTasksOnly=true`;
        
        const response = await fetch(url, {
          headers: {
            ...getOrganizationHeaders(),
          },
        });
        if (!response.ok) throw new Error('Failed to fetch categories');
        const data = sortCategoriesByLevelAndOrder(await response.json());
        setCategories(data);

        sessionStorage.setItem(categoryCacheKey, JSON.stringify(data));
        if (!expandedCategoriesInitialized.current) {
          setExpandedCategories(expandAllCategories(data));
          expandedCategoriesInitialized.current = true;
        }
        applySelectedCategoryFromUrl(data);
        
        setError(null);
      } catch (err) {
        setError('Failed to load categories');
        console.error(err);
      } finally {
        setCategoryLoading(false);
      }
    };

    // Fetch user subscription tier for this specific course if authenticated
    const fetchUserSubscriptionTier = async () => {
      if (isAuthenticated && user?.sub && courseIdFromURL) {
        try {
          const tierResponse = await fetch(`${config.apiBaseUrl}/api/user/subscription-tier/${courseIdFromURL}?sub=${user.sub}`);
          if (tierResponse.ok) {
            const tierData = await tierResponse.json();
            setUserSubscriptionTier(tierData.subscriptionTier || 1);
          } else {
            setUserSubscriptionTier(1); // Default to Free tier
          }
        } catch (err) {
          console.error('Error fetching user subscription tier:', err);
          setUserSubscriptionTier(1); // Default to Free tier on error
        }
      }
    };

    fetchCategories();
    fetchUserSubscriptionTier();
  }, [isAuthenticated, user?.sub, courseIdFromURL, selectedCategoryIdFromURL]);

  // Fetch last attempt status when a question is selected
  useEffect(() => {
    const fetchLastAttempt = async () => {
      if (!selectedQuestion || !isAuthenticated) return;

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${config.apiBaseUrl}/api/QuestionAttempts/question/${selectedQuestion.id}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const attempts = await response.json();
          if (attempts && attempts.length > 0) {
            // Get the most recent attempt (assuming they're sorted by date, or we sort them)
            const sortedAttempts = attempts.sort((a: any, b: any) => 
              new Date(b.attemptedAt).getTime() - new Date(a.attemptedAt).getTime()
            );
            const lastAttempt = sortedAttempts[0];
            setLastAttemptStatus({
              ...lastAttemptStatus,
              [selectedQuestion.id]: {
                isCorrect: lastAttempt.isCorrect,
                attemptedAt: lastAttempt.attemptedAt
              }
            });
          } else {
            setLastAttemptStatus({
              ...lastAttemptStatus,
              [selectedQuestion.id]: null
            });
          }
        }
      } catch (error) {
        console.error('Error fetching last attempt:', error);
      }
    };

    fetchLastAttempt();
  }, [selectedQuestion, isAuthenticated]);

  // Fetch questions when a category is selected
  useEffect(() => {
    if (!selectedCategory) {
      setQuestions([]);
      setSelectedQuestion(null);
      return;
    }

    const findCategoryById = (cats: Category[], categoryId: number): Category | null => {
      for (const cat of cats) {
        if (cat.id === categoryId) {
          return cat;
        }

        if (cat.subCategories && cat.subCategories.length > 0) {
          const found = findCategoryById(cat.subCategories, categoryId);
          if (found) {
            return found;
          }
        }
      }

      return null;
    };

    const selectedCategoryNode = findCategoryById(categories, selectedCategory);
    if (selectedCategoryNode?.subCategories && selectedCategoryNode.subCategories.length > 0) {
      setQuestions([]);
      setSelectedQuestion(null);
      return;
    }

    const fetchQuestions = async () => {
      const questionsCacheKey = `practice-questions-${selectedCategory}`;

      try {
        const cachedQuestions = sessionStorage.getItem(questionsCacheKey);
        if (cachedQuestions) {
          try {
            const parsedCached = JSON.parse(cachedQuestions) as Question[];
            if (Array.isArray(parsedCached) && parsedCached.length > 0) {
              setQuestions(parsedCached);
              setError(null);
              return;
            }
          } catch {
            sessionStorage.removeItem(questionsCacheKey);
          }
        }

        setLoading(true);
        const response = await fetch(`${config.apiBaseUrl}/api/questions/category/${selectedCategory}`);
        if (!response.ok) throw new Error('Failed to fetch questions');
        const data = await response.json();
        setQuestions(data);
        sessionStorage.setItem(questionsCacheKey, JSON.stringify(data));
        setError(null);
      } catch (err) {
        setError('Failed to load questions');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [selectedCategory, categories]);

  const getDifficultyColor = (level: number) => {
    switch (level) {
      case 1:
        return '#4caf50'; // Green
      case 2:
        return '#ff9800'; // Orange
      case 3:
        return '#f44336'; // Red
      default:
        return '#999';
    }
  };

  const getDifficultyLabel = (level: number) => {
    switch (level) {
      case 1:
        return 'Easy';
      case 2:
        return 'Medium';
      case 3:
        return 'Hard';
      default:
        return 'Unknown';
    }
  };

  // Request AI explanation for the question
  const handleGetAiExplanation = async () => {
    if (!selectedQuestion) return;
    
    try {
      setLoadingExplanation(true);
      setExplanationError(null);
      setAiExplanation(null);
      setAiExplanationJson(null);
      setAiExplanationSource(null);

      const response = await fetch(`${config.apiBaseUrl}/api/azureopenaitest/explain-question`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          questionId: selectedQuestion.id,
          question: selectedQuestion.questionForOpenAI || selectedQuestion.questionLatex || selectedQuestion.questionText,
          answer: selectedQuestion.answerLatex || selectedQuestion.answerText,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get explanation from AI');
      }

      const data = await response.json();
      const parsedJson = data.explanationJson && typeof data.explanationJson === 'object'
        ? (data.explanationJson as AiExplanationPayload)
        : null;

      setAiExplanation(data.explanation ?? null);
      setAiExplanationJson(parsedJson);
      setAiExplanationSource(data.explanationSource === 'json' ? 'json' : (parsedJson ? 'json' : 'text'));
    } catch (err) {
      console.error('Error getting AI explanation:', err);
      setExplanationError('Failed to get explanation. Please try again.');
    } finally {
      setLoadingExplanation(false);
    }
  };

  // Function to check if user has access to a category
  const checkCategoryAccess = (category: Category): { hasAccess: boolean; message: string } => {
    const categoryTier = category.accessibilityType || 1;
    
    if (!isAuthenticated) {
      if (categoryTier <= 1) {
        return { hasAccess: true, message: '' };
      }

      return {
        hasAccess: false,
        message: `📖 This category requires a ${getTierName(categoryTier)} subscription. Please log in or upgrade to access this content.`
      };
    }
    
    if (userSubscriptionTier < categoryTier) {
      return {
        hasAccess: false,
        message: `🔒 This category requires a ${getTierName(categoryTier)} subscription. You currently have a ${getTierName(userSubscriptionTier)} plan. Please upgrade to access this content.`
      };
    }
    
    return { hasAccess: true, message: '' };
  };

  const getTierName = (tier: number): string => {
    switch (tier) {
      case 1: return 'Free';
      case 2: return 'Bronze';
      case 3: return 'Silver';
      case 4: return 'Gold';
      default: return 'Free';
    }
  };
  const findCategoryById = (cats: Category[], categoryId: number): Category | null => {
    for (const cat of cats) {
      if (cat.id === categoryId) {
        return cat;
      }

      if (cat.subCategories && cat.subCategories.length > 0) {
        const found = findCategoryById(cat.subCategories, categoryId);
        if (found) {
          return found;
        }
      }
    }

    return null;
  };

  const selectedCategoryNode = selectedCategory ? findCategoryById(categories, selectedCategory) : null;
  const selectedCategoryHasSubcategories = !!(selectedCategoryNode?.subCategories && selectedCategoryNode.subCategories.length > 0);
  const selectedCategoryName = selectedCategoryNode?.name ?? 'Select a topic';
  const normalizedCategorySearch = categorySearch.trim().toLowerCase();
  const shouldShowRightQuestionsSection = !isSmallScreen || !!selectedCategory || !!selectedQuestion || showQuestionDetails || !!accessDeniedMessage;
  const isSmallNoCategorySelectedState = isSmallScreen && !!courseIdFromURL && !selectedCategory;
  const showQuestionListBackBar = (isMobile || isTablet) && !!selectedCategory && !selectedQuestion && questions.length > 0 && !showQuestionDetails;
  const showLockedQuestionsBackBar = (isMobile || isTablet) && !!selectedCategory && !selectedQuestion && questions.length === 0 && !selectedCategoryHasSubcategories && !showQuestionDetails;
  const showCategorySelectionBackBar = isSmallNoCategorySelectedState && !showQuestionDetails;
  const showQuestionDetailsBackBar = isSmallScreen && !!selectedQuestion && showQuestionDetails;
  const selectedQuestionIndex = selectedQuestion ? questions.findIndex((question) => question.id === selectedQuestion.id) + 1 : 0;
  const questionProgress = questions.length > 0 && selectedQuestionIndex > 0 ? Math.round((selectedQuestionIndex / questions.length) * 100) : 0;
  const selectedQuestionLabel = selectedQuestionIndex > 0 ? selectedQuestionIndex : selectedQuestion?.id ?? '';
  const previewQuestionLimit = isAuthenticated ? 5 : 10;
  const accessibleQuestionCount = userSubscriptionTier < 999 ? Math.min(questions.length, previewQuestionLimit) : questions.length;

  const handleBackToCourseDetailsOrCourses = () => {
    if (courseIdFromURL) {
      const categoryParam = selectedCategory ? `?categoryId=${selectedCategory}` : '';
      navigate(withOrganizationSlug(`/course-details/${courseIdFromURL}${categoryParam}`));
      return;
    }

    navigate(withOrganizationSlug('/courses'));
  };

  const handleBackFromQuestionList = () => {
    if (!selectedCategory) {
      handleBackToCourseDetailsOrCourses();
      return;
    }

    setSelectedCategory(null);
    setSelectedQuestion(null);
    setShowQuestionDetails(false);
    setShowTopicsMenu(true);
  };

  const filterCategories = (cats: Category[]): Category[] => {
    if (!normalizedCategorySearch) {
      return cats;
    }

    return cats.reduce<Category[]>((accumulator, cat) => {
      const filteredSubCategories = filterCategories(cat.subCategories || []);
      const matchesCategory =
        cat.name.toLowerCase().includes(normalizedCategorySearch) ||
        (cat.description || '').toLowerCase().includes(normalizedCategorySearch);

      if (matchesCategory || filteredSubCategories.length > 0) {
        accumulator.push({
          ...cat,
          subCategories: filteredSubCategories,
        });
      }

      return accumulator;
    }, []);
  };
  const filteredCategories = filterCategories(categories);
  const categoryMenuItems =
    filteredCategories.length === 1 && filteredCategories[0].subCategories?.length
      ? filteredCategories[0].subCategories
      : filteredCategories;

  const renderCategoryTree = (cats: Category[], level: number = 0) => {
    const sortedCategories = sortCategoriesByLevelAndOrder(cats);

    return sortedCategories.map(cat => (
      <div key={cat.id} style={{ marginBottom: 4, paddingLeft: `${level * 14}px` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {level > 0 ? (
            <div style={{ position: 'relative', height: 28, width: 14, flexShrink: 0 }}>
              <span
                style={{
                  position: 'absolute',
                  left: 4,
                  top: 0,
                  bottom: 0,
                  borderLeft: '1px dotted #d1d5db'
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  left: 4,
                  top: '50%',
                  width: 10,
                  borderTop: '1px dotted #d1d5db'
                }}
              />
            </div>
          ) : (
            <div style={{ height: 28, width: 2, flexShrink: 0 }} />
          )}

          <button
            onClick={() => {
              if (loading) {
                return;
              }

              const access = checkCategoryAccess(cat);
              if (!access.hasAccess) {
                setAccessDeniedMessage(access.message);
                setSelectedCategory(null);
                setSelectedQuestion(null);
                return;
              }

              setAccessDeniedMessage(null);

              if (cat.subCategories && cat.subCategories.length > 0) {
                const newExpanded = new Set(expandedCategories);
                if (newExpanded.has(cat.id)) {
                  newExpanded.delete(cat.id);
                } else {
                  newExpanded.add(cat.id);
                }
                setExpandedCategories(newExpanded);

                setSelectedCategory(cat.id);
                setSelectedQuestion(null);

                // On small screens, keep the sidebar open so subcategories are visible
                return;
              }

              setSelectedCategory(selectedCategory === cat.id ? null : cat.id);
              if (selectedCategory !== cat.id) {
                setSelectedQuestion(null);
              }

              if (isMobile || isTablet) {
                setShowTopicsMenu(false);
              }
            }}
            style={{
              width: '100%',
              paddingTop: 9,
              paddingBottom: 9,
              paddingRight: 8,
              paddingLeft: 10,
              cursor: loading ? 'not-allowed' : 'pointer',
              backgroundColor: selectedCategory === cat.id ? '#e3f2fd' : 'transparent',
              borderLeft: selectedCategory === cat.id ? '2px solid #2196f3' : '2px solid transparent',
              borderTop: 'none',
              borderRight: 'none',
              borderBottom: 'none',
              borderRadius: 6,
              fontSize: 14,
              fontWeight: selectedCategory === cat.id ? 600 : 500,
              color: selectedCategory === cat.id ? '#1976d2' : '#333',
              transition: 'all 0.2s ease',
              textAlign: 'left',
              opacity: loading ? 0.65 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
            aria-label={cat.subCategories && cat.subCategories.length > 0 ? 'Toggle category' : 'Select category'}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%', minWidth: 0 }}>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0 }}>{cat.name}</span>
              {cat.accessibilityType && cat.accessibilityType > 1 && (
                <span style={{ fontSize: '12px', opacity: 0.7, flexShrink: 0 }}>
                  {cat.accessibilityType === 2 ? '🥉' : cat.accessibilityType === 3 ? '🥈' : '🥇'}
                </span>
              )}
              {cat.subCategories && cat.subCategories.length > 0 && (
                <span style={{
                  fontSize: 11, fontWeight: 600, flexShrink: 0,
                  padding: '1px 7px', borderRadius: 10,
                  background: selectedCategory === cat.id ? '#bfdbfe' : '#f1f5f9',
                  color: selectedCategory === cat.id ? '#1d4ed8' : '#64748b',
                  border: '1px solid',
                  borderColor: selectedCategory === cat.id ? '#93c5fd' : '#e2e8f0',
                  whiteSpace: 'nowrap'
                }}>
                  {cat.subCategories.length} topics
                </span>
              )}
              {cat.subCategories && cat.subCategories.length > 0 ? (
                <span
                  style={{
                    color: selectedCategory === cat.id ? '#1976d2' : '#94a3b8',
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    transition: 'transform 0.2s ease',
                    transform: (expandedCategories.has(cat.id) || !!normalizedCategorySearch) ? 'rotate(90deg)' : 'rotate(0deg)'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 6l5 4-5 4" />
                  </svg>
                </span>
              ) : null}
            </div>
          </button>
        </div>
        {cat.subCategories && cat.subCategories.length > 0 && (expandedCategories.has(cat.id) || !!normalizedCategorySearch) && (
          renderCategoryTree(cat.subCategories, level + 1)
        )}
      </div>
    ));
  };

  return (
    <MathJaxContext>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .html-content h1, .html-content h2, .html-content h3, 
        .html-content h4, .html-content h5, .html-content h6 {
          margin: 12px 0 8px 0 !important;
          color: #222 !important;
          font-weight: 600 !important;
          text-align: left !important;
        }
        .html-content h1 { font-size: 20px !important; }
        .html-content h2 { font-size: 18px !important; }
        .html-content h3 { font-size: 16px !important; }
        .html-content h4 { font-size: 14px !important; }
        .html-content h5 { font-size: 13px !important; }
        .html-content h6 { font-size: 12px !important; }
        .html-content ul, .html-content ol {
          margin: 8px 0 !important;
          padding-left: 24px !important;
          text-align: left !important;
        }
        .html-content li {
          margin: 4px 0 !important;
          line-height: 1.6 !important;
          text-align: left !important;
        }
        .html-content p {
          margin: 8px 0 !important;
          text-align: left !important;
        }
        .html-content strong, .html-content b {
          font-weight: 600 !important;
          color: #1976d2 !important;
        }
        .html-content em, .html-content i {
          font-style: italic !important;
          color: #555 !important;
        }
        .html-content code {
          background: #e8f4f8 !important;
          padding: 2px 6px !important;
          border-radius: 3px !important;
          font-family: monospace !important;
          font-size: 12px !important;
        }
        .html-content pre {
          background: #f4f4f4 !important;
          padding: 12px !important;
          border-radius: 6px !important;
          overflow-x: auto !important;
          margin: 8px 0 !important;
        }
        .html-content a {
          color: #1976d2 !important;
          text-decoration: underline !important;
        }
        .html-content blockquote {
          border-left: 4px solid #2196f3 !important;
          padding-left: 12px !important;
          margin: 12px 0 !important;
          color: #555 !important;
          font-style: italic !important;
        }

        .question-list-item:hover,
        .question-list-item:active {
          background-color: #f5f7ff !important;
          border-color: #a5b4fc !important;
          border-left-color: #6366f1 !important;
          box-shadow: 0 4px 12px rgba(99,102,241,0.18) !important;
        }

        .show-on-small { display: none; }
        @media (max-width: 640px) {
          .hide-on-small { display: none; }
          .show-on-small { display: inline; }
        }

      `}</style>
      <div className="app-breadcrumb-bar">
        <div className="app-breadcrumb-row">
          <button onClick={() => navigate('/')} className="app-breadcrumb-link">Home</button>
          <span className="app-breadcrumb-sep">›</span>
          <button
            onClick={() => navigate(guestAwarePath('/courses'))}
            className="app-breadcrumb-link"
          >
            Courses
          </button>
          <span className="app-breadcrumb-sep">›</span>
          <span className="app-breadcrumb-current">Practice Questions</span>
        </div>
      </div>

      {error && (
        <div style={{
          background: '#ffebee',
          color: '#c62828',
          padding: isMobile ? 12 : 16,
          margin: isMobile ? '8px 0' : '8px 0',
          borderRadius: 8,
          fontSize: isMobile ? '0.9rem' : '1rem'
        }}>
          {error}
        </div>
      )}

      <div style={{
        display: 'flex',
        overflow: 'hidden',
        flexDirection: isMobile ? 'column' : 'row',
        position: 'relative',
        height: 'calc(100vh - 220px)',
        minHeight: 480
      }}>
        {/* Left Sidebar */}
        {(!((isMobile || isTablet) && showQuestionDetails) && (!(isMobile || isTablet) || showTopicsMenu)) && (
        <div style={{
          width: isMobile ? '100%' : isTablet ? 240 : 460,
          backgroundColor: '#f8fafc',
          padding: 0,
          overflow: 'hidden',
          order: -1,
          maxHeight: isSmallScreen ? '100%' : 'auto',
          height: isSmallScreen ? '100%' : '100%',
          minHeight: isSmallScreen ? 0 : (courseIdFromURL ? 420 : undefined),
          borderRadius: (isMobile || isTablet) ? '0 0 10px 10px' : 0,
          marginBottom: (isMobile || isTablet) ? 12 : 0
        }}>
          <div style={{
            padding: isMobile ? 12 : 16,
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}>
          {!courseIdFromURL ? (
            <div style={{
              padding: 12,
              textAlign: 'left',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              height: '100%'
            }}>
              <p style={{ 
                margin: '0 0 16px 0', 
                fontWeight: 600, 
                fontSize: isMobile ? '14px' : '16px',
                color: '#333',
                textAlign: 'left'
              }}>📚 Select a Course</p>
              
              {categoryLoading ? (
                <p style={{ color: '#999', fontSize: '13px' }}>Loading courses...</p>
              ) : (
                <div style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  paddingRight: isMobile ? 2 : 4
                }}>
                  {enrolledCourses.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <p style={{ 
                        fontSize: '12px', 
                        fontWeight: 'bold', 
                        color: '#2e7d32',
                        marginBottom: '8px',
                        textAlign: 'left'
                      }}>Your Enrolled Courses</p>
                      {enrolledCourses.map((enrollment: any) => (
                        <div
                          key={enrollment.id}
                          onClick={() => navigate(withOrganizationSlug(`/practice?courseId=${enrollment.courseId}`))}
                          style={{
                            padding: '10px 12px',
                            backgroundColor: '#e8f5e9',
                            border: '1px solid #a5b4fc',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            marginBottom: '8px',
                            boxShadow: '0 2px 6px rgba(99,102,241,0.1)',
                            transition: 'all 0.18s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#6366f1';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(99,102,241,0.2)';
                            e.currentTarget.style.backgroundColor = '#c8e6c9';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#a5b4fc';
                            e.currentTarget.style.boxShadow = '0 2px 6px rgba(99,102,241,0.1)';
                            e.currentTarget.style.backgroundColor = '#e8f5e9';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <div style={{ 
                            fontWeight: 600, 
                            fontSize: isMobile ? '13px' : '14px',
                            color: '#3730a3',
                            marginBottom: '4px',
                            textAlign: 'left'
                          }}>
                            {enrollment.courseName}
                          </div>
                          <div style={{
                            fontSize: '11px',
                            color: '#6366f1',
                            textAlign: 'left'
                          }}>
                            {Math.round(enrollment.completionPercentage)}% Complete
                            {enrollment.subscriptionTier >= 999 && ' • Full Access ✓'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {courses.length > 0 && (
                    <div>
                      <p style={{ 
                        fontSize: '12px',
                        fontWeight: 'bold', 
                        color: '#1976d2',
                        marginBottom: '8px',
                        textAlign: 'left'
                      }}>All Available Courses</p>
                      {courses.filter((course: any) => course.isActive).map((course: any) => (
                        <div
                          key={course.id}
                          onClick={() => navigate(withOrganizationSlug(`/practice?courseId=${course.id}`))}
                          style={{
                            padding: '10px 12px',
                            backgroundColor: 'rgba(238,242,255,0.4)',
                            border: '1px solid #a5b4fc',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            marginBottom: '8px',
                            boxShadow: '0 2px 6px rgba(99,102,241,0.1)',
                            transition: 'all 0.18s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#6366f1';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(99,102,241,0.2)';
                            e.currentTarget.style.backgroundColor = 'rgba(224,231,255,0.6)';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#a5b4fc';
                            e.currentTarget.style.boxShadow = '0 2px 6px rgba(99,102,241,0.1)';
                            e.currentTarget.style.backgroundColor = 'rgba(238,242,255,0.4)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <div style={{ 
                            fontWeight: 600, 
                            fontSize: isMobile ? '13px' : '14px',
                            color: '#3730a3',
                            marginBottom: '4px',
                            textAlign: 'left'
                          }}>
                            {course.name}
                          </div>
                          <div style={{
                            fontSize: '11px',
                            color: '#6366f1',
                            textAlign: 'left'
                          }}>
                            {course.difficultyLevel}
                            {enrolledCourses.some((e: any) => e.courseId === course.id) && ' • Enrolled ✓'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {courses.length === 0 && enrolledCourses.length === 0 && (
                    <p style={{ fontSize: '12px', color: '#999' }}>No courses available</p>
                  )}
                </div>
              )}
            </div>
          ) : categoryLoading && categories.length === 0 ? (
            <div style={{ minHeight: 360 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                color: '#1d4ed8',
                fontSize: isMobile ? '0.85rem' : '1rem',
                fontWeight: 600,
                marginBottom: 10
              }}>
                <span style={{
                  display: 'inline-block',
                  height: 14,
                  width: 14,
                  borderRadius: '50%',
                  border: '2px solid #3b82f6',
                  borderTopColor: 'transparent',
                  animation: 'spin 1s linear infinite'
                }} />
                Loading topics...
              </div>
            </div>
          ) : categories.length === 0 ? (
            <div style={{ minHeight: 360 }}>
              <p style={{ color: '#999', fontSize: isMobile ? '0.85rem' : '1rem' }}>No topics available</p>
            </div>
          ) : (
            <div style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: 14,
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
              overflow: 'hidden',
              height: '100%'
            }}>
              <div style={{
                padding: isMobile ? 12 : 14,
                backgroundColor: '#f8fafc',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 10
                }}>
                  <div style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    backgroundColor: '#e0e7ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                    flexShrink: 0
                  }}>
                    📚
                  </div>
                  <p style={{
                    margin: 0,
                    fontSize: 14,
                    fontWeight: 700,
                    color: '#111827',
                    textAlign: 'left'
                  }}>
                    Topics
                  </p>
                </div>
                <div style={{ position: 'relative', width: '100%' }}>
                  <span style={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#9ca3af',
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    value={categorySearch}
                    onChange={(e) => setCategorySearch(e.target.value)}
                    placeholder="Search topics..."
                    style={{
                      width: '100%',
                      padding: '10px 12px 10px 40px',
                      borderRadius: 8,
                      border: '1px solid #d1d5db',
                      fontSize: 14,
                      outline: 'none',
                      boxSizing: 'border-box',
                      backgroundColor: '#fff'
                    }}
                  />
                </div>
              </div>
              <div style={{
                padding: isMobile ? 10 : 12,
                maxHeight: 'calc(100% - 72px)',
                height: 'calc(100% - 72px)',
                overflowY: 'auto',
                backgroundColor: '#ffffff'
              }}>
                {loading && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    color: '#1d4ed8',
                    fontSize: isMobile ? '0.8rem' : '0.9rem',
                    fontWeight: 600,
                    marginBottom: 10
                  }}>
                    <span style={{
                      display: 'inline-block',
                      height: 12,
                      width: 12,
                      borderRadius: '50%',
                      border: '2px solid #3b82f6',
                      borderTopColor: 'transparent',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Loading selected topic...
                  </div>
                )}
                {renderCategoryTree(categoryMenuItems)}
                {normalizedCategorySearch && categoryMenuItems.length === 0 && (
                  <div style={{
                    padding: '14px 12px',
                    borderRadius: 8,
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    color: '#6b7280',
                    fontSize: 13
                  }}>
                    No topics match "{categorySearch.trim()}".
                  </div>
                )}
              </div>
            </div>
          )}
          </div>
        </div>
        )}

        {/* Right Content - Questions */}
        {shouldShowRightQuestionsSection && (
        <div style={{ 
          flex: 1, 
          minWidth: 0,
          minHeight: 0,
          overflowY: 'auto',
          padding: isMobile ? 8 : 16,
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Access Denied Message */}
          {accessDeniedMessage && (
            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: 8,
              padding: 20,
              marginBottom: 20,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 16, fontWeight: 600, color: '#856404', marginBottom: 8 }}>
                  🔒 Upgrade Required
                </p>
                <p style={{ fontSize: 14, color: '#856404', lineHeight: 1.6, marginBottom: 16 }}>
                  {accessDeniedMessage}
                </p>
                <div style={{ display: 'flex', gap: 12 }}>
                  {!isAuthenticated && (
                    <button 
                      onClick={() => loginWithRedirect?.()}
                      style={{
                        backgroundColor: '#ffc107',
                        color: '#333',
                        border: 'none',
                        borderRadius: 4,
                        padding: '8px 16px',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 600,
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#ffb300')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffc107')}
                    >
                      Log In
                    </button>
                  )}
                  <button 
                    onClick={() => window.location.href = `/billing?courseId=${courseIdFromURL}`}
                    style={{
                      backgroundColor: '#4caf50',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      padding: '8px 16px',
                      cursor: 'pointer',
                      fontSize: 14,
                      fontWeight: 600,
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#45a049')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#4caf50')}
                  >
                    Upgrade Now
                  </button>
                </div>
              </div>
              <button 
                onClick={() => setAccessDeniedMessage(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: '#856404',
                  padding: '0 12px'
                }}
              >
                ✕
              </button>
            </div>
          )}
          
          {!selectedCategory ? (
            (isMobile || isTablet) ? null : (
              <div style={{
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                padding: 48,
                textAlign: 'center',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <p style={{ fontSize: 42, marginBottom: 12 }}>📚</p>
                <p style={{ fontSize: 18, color: '#334155', marginBottom: 12, fontWeight: 600 }}>
                  Select a topic to get started
                </p>
                <p style={{ fontSize: 14, color: '#64748b' }}>
                  Choose from the categories on the left to view practice questions
                </p>
              </div>
            )
          ) : loading ? (
            <div style={{
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: 10,
              padding: 48,
              textAlign: 'center',
              flex: 1
            }}>
              <p style={{ fontSize: 16, color: '#334155', fontWeight: 600 }}>Loading questions...</p>
            </div>
          ) : (
            <div style={{ flex: 1, minHeight: 0 }}>
              {/* Questions List */}
              {questions.length === 0 ? (
                <div style={{
                  display: selectedCategoryHasSubcategories ? 'flex' : 'block',
                  gap: selectedCategoryHasSubcategories ? (isMobile ? 12 : 20) : 0,
                  flexDirection: selectedCategoryHasSubcategories ? (isMobile || isTablet ? 'column' : 'row') : undefined,
                  height: isSmallScreen ? 'auto' : '100%'
                }}>
                  <div style={{
                    flex: selectedCategoryHasSubcategories
                      ? (isMobile || isTablet ? '1 1 100%' : '0 0 550px')
                      : '1 1 100%',
                    width: '100%'
                  }}>
                    <h3 style={{
                      fontSize: isMobile ? 14 : 18,
                      fontWeight: 600,
                      marginBottom: 12,
                      color: '#222'
                    }}>
                      Questions
                    </h3>

                    {!selectedCategoryHasSubcategories ? (
                      <div style={{
                        background: '#fff7ed',
                        border: '1px solid #fdba74',
                        borderRadius: 8,
                        padding: 28,
                        textAlign: 'center'
                      }}>
                        <p style={{ fontSize: 18, color: '#9a3412', fontWeight: 600, marginBottom: 10 }}>
                          🔒 Questions are currently locked for this section
                        </p>
                        <p style={{ fontSize: 14, color: '#7c2d12', marginBottom: 16, lineHeight: 1.6 }}>
                          To unlock questions, please upgrade your plan or complete the previous sections first.
                          Questions will be available once those requirements are met.
                        </p>
                        {courseIdFromURL && (
                          <button
                            onClick={() => window.location.href = `/billing?courseId=${courseIdFromURL}`}
                            style={{
                              backgroundColor: '#f97316',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 8,
                              padding: '10px 18px',
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            Upgrade Now
                          </button>
                        )}
                      </div>
                    ) : (
                      <div style={{
                        background: '#f5f5f5',
                        borderRadius: 8,
                        padding: 24,
                        textAlign: 'center'
                      }}>
                        <p style={{ fontSize: 14, color: '#666' }}>
                          Expand a subcategory to view and practice questions.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Category details column */}
                  {selectedCategoryHasSubcategories && (
                  <div style={{
                    flex: 1,
                    minWidth: 0
                  }}>
                  {selectedCategory && categories.length > 0 && (() => {
                    const findCategory = (cats: Category[]): Category | undefined => {
                      for (const cat of cats) {
                        if (cat.id === selectedCategory) return cat;
                        if (cat.subCategories) {
                          const found = findCategory(cat.subCategories);
                          if (found) return found;
                        }
                      }
                      return undefined;
                    };
                    const selectedCat = findCategory(categories);
                    
                    if (selectedCat?.detailedDescription || selectedCat?.example) {
                      return (
                        <div style={{
                          maxWidth: '100%',
                          width: '100%',
                          margin: 0,
                          padding: '24px 28px',
                          backgroundColor: '#ffffff',
                          borderRadius: 10,
                          boxShadow: '0 4px 14px rgba(0, 0, 0, 0.06)',
                          marginBottom: 0
                        }}>
                          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 20, color: '#222', textAlign: 'left' }}>
                            {selectedCat?.name}
                          </h2>

                          {renderDiagramsByPlacement(selectedCat, 1)}
                          {renderDynamicComponentsByPlacement(selectedCat, 1)}

                          {/* Detailed Description */}
                          {selectedCat?.detailedDescription && !(isMobile || isTablet) && (
                            <div style={{ marginBottom: 16 }}>
                              <button
                                onClick={() => setShowDetailedDescInQuestion(!showDetailedDescInQuestion)}
                                style={{
                                  width: '100%',
                                  padding: '10px 12px',
                                  backgroundColor: '#e3f2fd',
                                  color: '#1976d2',
                                  border: '1px solid #2196f3',
                                  borderRadius: 6,
                                  cursor: 'pointer',
                                  fontSize: 13,
                                  fontWeight: 600,
                                  textAlign: 'left',
                                  marginBottom: 8
                                }}
                              >
                                {showDetailedDescInQuestion ? '▲ Hide Details' : '▼ Show Details'}
                              </button>
                              <div style={{
                                padding: 12,
                                paddingLeft: 32,
                                backgroundColor: '#f5f9ff',
                                borderRadius: 6,
                                fontSize: 13,
                                lineHeight: 1.6,
                                textAlign: 'left'
                              }}>
                                <div 
                                  className="html-content"
                                  dangerouslySetInnerHTML={{ __html: selectedCat.detailedDescription }} 
                                />
                              </div>
                            </div>
                          )}

                          {selectedCat?.tips && !(isMobile || isTablet) && (
                            <div style={{ marginBottom: 16 }}>
                              <div
                                style={{
                                  width: '100%',
                                  padding: '10px 12px',
                                  backgroundColor: '#fff7ed',
                                  color: '#c2410c',
                                  border: '1px solid #fdba74',
                                  borderRadius: 6,
                                  fontSize: 13,
                                  fontWeight: 600,
                                  textAlign: 'left',
                                  marginBottom: 8,
                                }}
                              >
                                Tips
                              </div>
                              <div style={{
                                padding: 12,
                                paddingLeft: 32,
                                backgroundColor: '#fffaf5',
                                borderRadius: 6,
                                fontSize: 13,
                                lineHeight: 1.6,
                                textAlign: 'left'
                              }}>
                                <div className="html-content" dangerouslySetInnerHTML={{ __html: selectedCat.tips }} />
                              </div>
                            </div>
                          )}

                          {renderDiagramsByPlacement(selectedCat, 2)}
                          {renderDynamicComponentsByPlacement(selectedCat, 2)}
                          {renderDiagramsByPlacement(selectedCat, 3)}
                          {renderDynamicComponentsByPlacement(selectedCat, 3)}

                          {/* Example */}
                          {selectedCat?.example && (
                            <div>
                              <button
                                onClick={() => setShowExampleInQuestion(!showExampleInQuestion)}
                                style={{
                                  width: '100%',
                                  padding: '10px 12px',
                                  backgroundColor: '#ecfdf5',
                                  color: '#10b981',
                                  border: '1px solid #10b981',
                                  borderRadius: 6,
                                  cursor: 'pointer',
                                  fontSize: 13,
                                  fontWeight: 600,
                                  textAlign: 'left',
                                  marginBottom: 8
                                }}
                              >
                                {showExampleInQuestion ? '▲ Hide Examples' : '▼ Show Examples'}
                              </button>
                              {showExampleInQuestion && (
                                <MathJax>
                                  <div style={{
                                    padding: 12,
                                    paddingLeft: 32,
                                    backgroundColor: '#f0fdf4',
                                    borderRadius: 6,
                                    fontSize: 13,
                                    lineHeight: 1.6,
                                    textAlign: 'left'
                                  }}>
                                    <div 
                                      className="html-content"
                                      dangerouslySetInnerHTML={{ __html: convertLatexToHtml(selectedCat.example || '') }} 
                                    />
                                  </div>
                                </MathJax>
                              )}
                            </div>
                          )}

                          {renderDiagramsByPlacement(selectedCat, 4)}
                          {renderDynamicComponentsByPlacement(selectedCat, 4)}
                          {renderDiagramsByPlacement(selectedCat, 5)}
                          {renderDynamicComponentsByPlacement(selectedCat, 5)}
                        </div>
                      );
                    }
                    return null;
                  })()}
                  </div>
                  )}
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  gap: isMobile ? 12 : 20,
                  flexDirection: isMobile || isTablet ? 'column' : 'row',
                  alignItems: isMobile || isTablet ? 'stretch' : 'stretch',
                  height: '100%',
                  minHeight: 0
                }}>
                  {/* Questions Column - Hide on mobile/tablet when details shown */}
                  {!((isMobile || isTablet) && showQuestionDetails) && (
                  <div style={{
                    flex: isMobile || isTablet ? '1 1 100%' : '0 0 530px',
                    backgroundColor: '#fff',
                    borderRadius: 10,
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0
                  }}>
                    <div style={{
                      padding: isMobile ? '12px 14px' : '14px 16px',
                      backgroundColor: '#f8fafc',
                      borderBottom: '1px solid #e5e7eb',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      flexShrink: 0
                    }}>
                      <button
                          onClick={() => {
                            if (isMobile || isTablet) {
                              handleBackFromQuestionList();
                              return;
                            }

                            handleBackToCourseDetailsOrCourses();
                          }}
                          aria-label="Back"
                          title="Back"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '6px 8px',
                            borderRadius: 8,
                            border: '1px solid #e5e7eb',
                            background: '#fff',
                            cursor: 'pointer',
                            color: '#374151',
                            flexShrink: 0
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 6l-5 4 5 4" />
                          </svg>
                        </button>                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'left' }}>
                        <h3 style={{
                          fontSize: isMobile ? 14 : 18,
                          fontWeight: 600,
                          margin: 0,
                          color: '#222',
                          textAlign: 'left'
                        }}>
                          {questions.length} Questions
                        </h3>
                        {selectedCategoryName && selectedCategoryName !== 'Select a topic' && (
                          <p style={{
                            margin: 0,
                            fontSize: isMobile ? 11 : 12,
                            color: '#6366f1',
                            fontWeight: 500,
                            textAlign: 'left'
                          }}>
                            {selectedCategoryName}
                          </p>
                        )}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      padding: isMobile ? 10 : 12,
                      flex: 1,
                      minHeight: 0,
                      overflowY: 'auto',
                      backgroundColor: '#fff'
                    }}>
                      {questions.map((q, index) => {
                        const previewLimit = isAuthenticated ? 5 : 10;
                        const isLocked = index >= previewLimit && userSubscriptionTier < 999; // Tier 999 = full access
                        
                        return (
                        <div
                          key={q.id}
                          onClick={() => {
                            if (isLocked) {
                              // Show upgrade prompt
                              setAccessDeniedMessage(`🔒 This question is locked. Upgrade to access all ${questions.length} questions in this category.`);
                              return;
                            }
                            setSelectedQuestion(q);
                            setAiExplanation(null);
                            setExplanationError(null);
                            setLoadingExplanation(false);
                            // On mobile/tablet, show details view
                            if (isMobile || isTablet) {
                              setShowQuestionDetails(true);
                            }
                          }}
                          className={!isLocked ? 'question-list-item' : ''}
                          style={{
                            padding: isMobile ? '10px 12px' : '13px 16px',
                            paddingLeft: isMobile ? 10 : 14,
                            backgroundColor: selectedQuestion?.id === q.id ? '#eff6ff' : '#fff',
                            border: selectedQuestion?.id === q.id
                              ? '1.5px solid #3b82f6'
                              : isLocked ? '1px solid #fcd34d' : '1px solid #c7d2fe',
                            borderLeft: selectedQuestion?.id === q.id
                              ? '4px solid #2563eb'
                              : isLocked ? '4px solid #f59e0b' : '4px solid #a5b4fc',
                            borderRadius: 8,
                            cursor: isLocked ? 'not-allowed' : 'pointer',
                            transition: 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
                            display: 'flex',
                            gap: isMobile ? 8 : 12,
                            alignItems: 'flex-start',
                            opacity: isLocked ? 0.65 : 1,
                            position: 'relative',
                            filter: isLocked ? 'blur(2px)' : 'none',
                            boxShadow: selectedQuestion?.id === q.id
                              ? '0 2px 8px rgba(37,99,235,0.15)'
                              : '0 1px 2px rgba(0,0,0,0.04)',  
                          }}
                        >
                          {isLocked && (
                            <div style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              fontSize: 24,
                              filter: 'blur(0px)',
                              zIndex: 1
                            }}>
                              🔒
                            </div>
                          )}
                          <p style={{
                            fontSize: isMobile ? 13 : 15,
                            fontWeight: 700,
                            color: selectedQuestion?.id === q.id ? '#2563eb' : isLocked ? '#9ca3af' : '#6366f1',
                            minWidth: isMobile ? '28px' : '36px',
                            flexShrink: 0,
                            margin: 0,
                            paddingTop: 1
                          }}>
                            Q{index + 1}
                          </p>
                          <div style={{
                            fontSize: isMobile ? 13 : 14,
                            color: isLocked ? '#9ca3af' : selectedQuestion?.id === q.id ? '#1e3a5f' : '#374151',
                            lineHeight: 1.6,
                            flex: 1,
                            fontWeight: selectedQuestion?.id === q.id ? 500 : 400,
                          }}>
                            <span>{stripHtml(q.questionText)}</span>
                            {q.questionLatex && (
                              <div style={{ marginTop: 8 }}>
                                <MathJax>{`\\(${q.questionLatex}\\)`}</MathJax>
                              </div>
                            )}
                          </div>
                        </div>
                        );
                      })}
                      
                      {/* Upgrade Prompt for Free Users */}
                      {questions.length > (isAuthenticated ? 5 : 10) && userSubscriptionTier < 999 && (
                        <div style={{
                          marginTop: 16,
                          padding: 20,
                          backgroundColor: '#fff3cd',
                          border: '2px solid #ffc107',
                          borderRadius: 8,
                          textAlign: 'center'
                        }}>
                          <p style={{ fontSize: 16, fontWeight: 600, color: '#856404', marginBottom: 8 }}>
                            🔒 {questions.length - (isAuthenticated ? 5 : 10)} More Questions Available
                          </p>
                          <p style={{ fontSize: 14, color: '#856404', marginBottom: 16, lineHeight: 1.6 }}>
                            You're viewing {isAuthenticated ? 5 : 10} of {questions.length} questions. Upgrade to unlock all questions and get full access to the course.
                          </p>
                          <button 
                            onClick={() => window.location.href = `/billing?courseId=${courseIdFromURL}`}
                            style={{
                              backgroundColor: '#4caf50',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 6,
                              padding: '12px 24px',
                              cursor: 'pointer',
                              fontSize: 15,
                              fontWeight: 600,
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#45a049')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#4caf50')}
                          >
                            Upgrade Now
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  )}

                  {/* Category Details Panel - Show even without selected question */}
                  {selectedCategory && categories.length > 0 && !selectedQuestion && !(isMobile || isTablet) && (() => {
                    const findCategory = (cats: Category[]): Category | undefined => {
                      for (const cat of cats) {
                        if (cat.id === selectedCategory) return cat;
                        if (cat.subCategories) {
                          const found = findCategory(cat.subCategories);
                          if (found) return found;
                        }
                      }
                      return undefined;
                    };
                    const selectedCat = findCategory(categories);
                    
                    if (selectedCat?.detailedDescription || selectedCat?.example) {
                      return (
                        <div style={{
                          flex: isMobile || isTablet ? '1 1 100%' : '1',
                          backgroundColor: '#fff',
                          borderRadius: 10,
                          border: '1px solid #e5e7eb',
                          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
                          padding: isMobile ? 16 : 24,
                          maxHeight: isMobile || isTablet ? 'calc(100vh - 100px)' : '100%',
                          height: isMobile || isTablet ? 'auto' : '100%',
                          overflowY: 'auto'
                        }}>
                          {/* Back Button for Mobile */}
                          {(isMobile || isTablet) && (
                            <button
                              onClick={() => {
                                setShowQuestionDetails(false);
                                setSelectedCategory(null);
                              }}
                              style={{
                                marginBottom: 16,
                                padding: '8px 12px',
                                backgroundColor: '#e0e7ff',
                                color: '#3730a3',
                                border: '1px solid #c7d2fe',
                                borderRadius: 6,
                                cursor: 'pointer',
                                fontSize: 13,
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                transition: 'background-color 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = '#c7d2fe';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = '#e0e7ff';
                              }}
                            >
                              ← Back to Categories
                            </button>
                          )}

                          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20, color: '#222' }}>
                            {selectedCat?.name}
                          </h2>

                          {renderDiagramsByPlacement(selectedCat, 1)}
                          {renderDynamicComponentsByPlacement(selectedCat, 1)}

                          {/* Detailed Description */}
                          {selectedCat?.detailedDescription && !(isMobile || isTablet) && (
                            <div style={{ marginBottom: 16 }}>
                              <button
                                onClick={() => setShowDetailedDescInQuestion(!showDetailedDescInQuestion)}
                                style={{
                                  width: '100%',
                                  padding: '10px 12px',
                                  backgroundColor: '#e3f2fd',
                                  color: '#1976d2',
                                  border: '1px solid #2196f3',
                                  borderRadius: 6,
                                  cursor: 'pointer',
                                  fontSize: 13,
                                  fontWeight: 600,
                                  textAlign: 'left',
                                  marginBottom: 8
                                }}
                              >
                                {showDetailedDescInQuestion ? '▲ Hide Details' : '▼ Show Details'}
                              </button>
                              {showDetailedDescInQuestion && (
                                <div style={{
                                  padding: 12,
                                  paddingLeft: 32,
                                  backgroundColor: '#f5f9ff',
                                  borderRadius: 6,
                                  fontSize: 13,
                                  lineHeight: 1.6,
                                  maxHeight: 'none',
                                  overflowY: 'auto',
                                  textAlign: 'left'
                                }}>
                                  <div className="html-content" dangerouslySetInnerHTML={{ __html: selectedCat.detailedDescription }} />
                                </div>
                              )}
                            </div>
                          )}

                          {selectedCat?.tips && !(isMobile || isTablet) && (
                            <div style={{ marginBottom: 16 }}>
                              <div
                                style={{
                                  width: '100%',
                                  padding: '10px 12px',
                                  backgroundColor: '#fff7ed',
                                  color: '#c2410c',
                                  border: '1px solid #fdba74',
                                  borderRadius: 6,
                                  fontSize: 13,
                                  fontWeight: 600,
                                  textAlign: 'left',
                                  marginBottom: 8,
                                }}
                              >
                                Tips
                              </div>
                              <div style={{
                                padding: 12,
                                paddingLeft: 32,
                                backgroundColor: '#fffaf5',
                                borderRadius: 6,
                                fontSize: 13,
                                lineHeight: 1.6,
                                textAlign: 'left'
                              }}>
                                <div className="html-content" dangerouslySetInnerHTML={{ __html: selectedCat.tips }} />
                              </div>
                            </div>
                          )}

                          {renderDiagramsByPlacement(selectedCat, 2)}
                          {renderDynamicComponentsByPlacement(selectedCat, 2)}
                          {renderDiagramsByPlacement(selectedCat, 3)}
                          {renderDynamicComponentsByPlacement(selectedCat, 3)}

                          {/* Example */}
                          {selectedCat?.example && !(isMobile || isTablet) && (
                            <div>
                              <button
                                onClick={() => setShowExampleInQuestion(!showExampleInQuestion)}
                                style={{
                                  width: '100%',
                                  padding: '10px 12px',
                                  backgroundColor: '#ecfdf5',
                                  color: '#10b981',
                                  border: '1px solid #10b981',
                                  borderRadius: 6,
                                  cursor: 'pointer',
                                  fontSize: 13,
                                  fontWeight: 600,
                                  textAlign: 'left',
                                  marginBottom: 8
                                }}
                              >
                                {showExampleInQuestion ? '▲ Hide Examples' : '▼ Show Examples'}
                              </button>
                              {showExampleInQuestion && (
                                <MathJax>
                                  <div style={{
                                    padding: 12,
                                    paddingLeft: 32,
                                    backgroundColor: '#f0fdf4',
                                    borderRadius: 6,
                                    fontSize: 13,
                                    lineHeight: 1.6,
                                    maxHeight: 'none',
                                    overflowY: 'auto',
                                    textAlign: 'left'
                                  }}>
                                    <div className="html-content" dangerouslySetInnerHTML={{ __html: convertLatexToHtml(selectedCat.example || '') }} />
                                  </div>
                                </MathJax>
                              )}
                            </div>
                          )}

                          {renderDiagramsByPlacement(selectedCat, 4)}
                          {renderDynamicComponentsByPlacement(selectedCat, 4)}
                          {renderDiagramsByPlacement(selectedCat, 5)}
                          {renderDynamicComponentsByPlacement(selectedCat, 5)}
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Question Detail */}
                  {selectedQuestion && (
                    <div key={`question-detail-${selectedQuestion.id}`} style={{
                      flex: isMobile || isTablet ? '1 1 100%' : '1',
                      backgroundColor: '#fff',
                      borderRadius: 10,
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
                      padding: isMobile ? 16 : 24,
                      maxHeight: isMobile || isTablet ? 'calc(100vh - 160px)' : '100%',
                      height: isMobile || isTablet ? 'calc(100vh - 160px)' : '100%',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 4, paddingBottom: isSmallScreen ? 64 : 0 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 16
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                          {(isMobile || isTablet) && (
                            <button
                              onClick={() => {
                                setShowQuestionDetails(false);
                                setSelectedQuestion(null);
                              }}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '5px 8px',
                                borderRadius: 7,
                                border: '1.5px solid #c7d2fe',
                                background: '#eef2ff',
                                color: '#4f46e5',
                                cursor: 'pointer',
                                flexShrink: 0
                              }}
                              aria-label="Back to questions"
                              title="Back to questions"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13 6l-5 4 5 4" />
                              </svg>
                            </button>
                          )}

                          <h3 style={{
                            fontSize: isMobile ? 16 : 18,
                            fontWeight: 400,
                            margin: 0,
                            color: '#222',
                            textAlign: 'left'
                          }}>
                            {showCategoryHelp ? (
                              <span style={{ color: '#4f46e5', fontWeight: 600 }}>
                                {selectedCategoryNode?.name ?? 'Category Help'}
                              </span>
                            ) : (
                              <>
                                <span className="hide-on-small">Question </span>
                                <span className="show-on-small">Q</span>
                                {selectedQuestionLabel}
                              </>
                            )}
                          </h3>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {/* Help button */}
                          {selectedCategoryNode && (
                            <button
                              onClick={() => setShowCategoryHelp(!showCategoryHelp)}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                padding: '5px 10px',
                                borderRadius: 7,
                                border: showCategoryHelp ? '1.5px solid #4f46e5' : '1.5px solid #c7d2fe',
                                background: showCategoryHelp ? '#eef2ff' : '#fff',
                                color: showCategoryHelp ? '#4f46e5' : '#6366f1',
                                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                transition: 'all 0.15s'
                              }}
                            >
                              {showCategoryHelp ? (
                                <>
                                  <svg xmlns="http://www.w3.org/2000/svg" style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                  Close
                                </>
                              ) : (
                                <>📚 Help</>
                              )}
                            </button>
                          )}
                          {/* Hint button */}
                          {selectedQuestion.hint && (
                            <button
                              onClick={() => setShowHintInline(!showHintInline)}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                padding: '5px 10px',
                                borderRadius: 7,
                                border: showHintInline ? '1.5px solid #16a34a' : '1.5px solid #bbf7d0',
                                background: showHintInline ? '#dcfce7' : '#fff',
                                color: showHintInline ? '#15803d' : '#16a34a',
                                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                transition: 'all 0.15s'
                              }}
                            >
                              💡 Hint
                            </button>
                          )}
                          {/* Report button */}
                          <button
                            onClick={() => { setShowReportPanel(!showReportPanel); setReportSubmitted(false); }}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '5px 10px',
                              borderRadius: 7,
                              border: showReportPanel ? '1.5px solid #dc2626' : '1.5px solid #fca5a5',
                              background: showReportPanel ? '#fee2e2' : '#fff',
                              color: showReportPanel ? '#dc2626' : '#ef4444',
                              fontSize: 13, fontWeight: 600, cursor: 'pointer',
                              transition: 'all 0.15s'
                            }}
                          >
                            🚩 Report
                          </button>
                          {/* Last Attempt Status Indicator */}
                          {lastAttemptStatus[selectedQuestion.id] && (
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '6px 12px', borderRadius: 6,
                              backgroundColor: lastAttemptStatus[selectedQuestion.id]?.isCorrect ? '#e8f5e9' : '#ffebee',
                              border: '2px solid',
                              borderColor: lastAttemptStatus[selectedQuestion.id]?.isCorrect ? '#4caf50' : '#f44336'
                            }}>
                              <div style={{ fontSize: 16, fontWeight: 'bold', color: lastAttemptStatus[selectedQuestion.id]?.isCorrect ? '#4caf50' : '#f44336' }}>
                                {lastAttemptStatus[selectedQuestion.id]?.isCorrect ? '✓' : '✗'}
                              </div>
                              {!(isSmallScreen && !lastAttemptStatus[selectedQuestion.id]?.isCorrect) && (
                                <div style={{ fontSize: 11, color: lastAttemptStatus[selectedQuestion.id]?.isCorrect ? '#2e7d32' : '#c62828', fontWeight: 600 }}>
                                  Last: {lastAttemptStatus[selectedQuestion.id]?.isCorrect ? 'Correct' : 'Incorrect'}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Inline report panel */}
                      {showReportPanel && (
                        <div style={{
                          marginBottom: 20, padding: 16,
                          backgroundColor: '#fff5f5', border: '1.5px solid #fca5a5',
                          borderRadius: 8, textAlign: 'left'
                        }}>
                          {reportSubmitted ? (
                            <p style={{ fontSize: 14, color: '#166534', fontWeight: 600, margin: 0 }}>✅ Thank you! Your report has been submitted.</p>
                          ) : (
                            <>
                              <p style={{ fontSize: 12, color: '#dc2626', fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>🚩 Report an Issue</p>
                              <div style={{ marginBottom: 10 }}>
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#7f1d1d', display: 'block', marginBottom: 4 }}>Issue type</label>
                                <select
                                  value={reportIssueType}
                                  onChange={e => setReportIssueType(e.target.value)}
                                  style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #fca5a5', fontSize: 13, background: '#fff', color: '#1e293b' }}
                                >
                                  <option>Wrong answer</option>
                                  <option>Incorrect question text</option>
                                  <option>Typo / formatting error</option>
                                  <option>Unclear question</option>
                                  <option>Other</option>
                                </select>
                              </div>
                              <div style={{ marginBottom: 12 }}>
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#7f1d1d', display: 'block', marginBottom: 4 }}>Details (optional)</label>
                                <textarea
                                  value={reportDetails}
                                  onChange={e => setReportDetails(e.target.value)}
                                  maxLength={2000}
                                  rows={3}
                                  placeholder="Describe the issue..."
                                  style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #fca5a5', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                                />
                              </div>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                  disabled={reportSubmitting}
                                  onClick={async () => {
                                    if (!selectedQuestion) return;
                                    setReportSubmitting(true);
                                    try {
                                      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
                                      if (isAuthenticated) {
                                        const token = await getAccessTokenSilently();
                                        headers['Authorization'] = `Bearer ${token}`;
                                      }
                                      await fetch(`${config.apiBaseUrl}/api/questionreports`, {
                                        method: 'POST',
                                        headers,
                                        body: JSON.stringify({ questionId: selectedQuestion.id, issueType: reportIssueType, details: reportDetails })
                                      });
                                      setReportSubmitted(true);
                                    } catch {}
                                    setReportSubmitting(false);
                                  }}
                                  style={{
                                    padding: '6px 16px', borderRadius: 6,
                                    background: reportSubmitting ? '#fca5a5' : '#dc2626',
                                    color: '#fff', border: 'none',
                                    fontSize: 13, fontWeight: 600, cursor: reportSubmitting ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  {reportSubmitting ? 'Submitting...' : 'Submit'}
                                </button>
                                <button
                                  onClick={() => setShowReportPanel(false)}
                                  style={{
                                    padding: '6px 16px', borderRadius: 6,
                                    background: '#fff', color: '#6b7280',
                                    border: '1px solid #d1d5db',
                                    fontSize: 13, fontWeight: 600, cursor: 'pointer'
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      {/* Inline hint panel */}
                      {showHintInline && selectedQuestion.hint && (
                        <div style={{
                          marginBottom: 20, padding: 12,
                          backgroundColor: '#dcfce7', border: '1.5px solid #16a34a',
                          borderRadius: 8, textAlign: 'left'
                        }}>
                          <p style={{ fontSize: 12, color: '#15803d', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase' }}>💡 Hint</p>
                          <p style={{ fontSize: 14, color: '#14532d', lineHeight: 1.6, margin: 0 }}>{selectedQuestion.hint}</p>
                        </div>
                      )}

                      {/* Category help panel */}
                      {showCategoryHelp && selectedCategoryNode ? (
                        <div style={{ textAlign: 'left' }}>
                          {selectedCategoryNode.description?.trim() && (
                            <div style={{ borderLeft: '4px solid #818cf8', paddingLeft: 12, marginBottom: 16 }}>
                              <div className="html-content" style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}
                                dangerouslySetInnerHTML={{ __html: selectedCategoryNode.description }} />
                            </div>
                          )}
                          {renderDiagramsByPlacement(selectedCategoryNode, 1)}
                          {renderDynamicComponentsByPlacement(selectedCategoryNode, 1)}
                          {selectedCategoryNode.detailedDescription?.trim() && (
                            <div style={{ marginBottom: 16 }}>
                              <div style={{ padding: '8px 12px', backgroundColor: '#eef2ff', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#4f46e5', marginBottom: 8 }}>Details</div>
                              <div className="html-content" style={{ padding: '10px 14px', backgroundColor: '#f5f7ff', borderRadius: 6, fontSize: 13, lineHeight: 1.6 }}
                                dangerouslySetInnerHTML={{ __html: selectedCategoryNode.detailedDescription }} />
                            </div>
                          )}
                          {selectedCategoryNode.tips?.trim() && (
                            <div style={{ marginBottom: 16 }}>
                              <div style={{ padding: '8px 12px', backgroundColor: '#fff7ed', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#c2410c', marginBottom: 8 }}>Tips</div>
                              <div className="html-content" style={{ padding: '10px 14px', backgroundColor: '#fffaf5', borderRadius: 6, fontSize: 13, lineHeight: 1.6 }}
                                dangerouslySetInnerHTML={{ __html: selectedCategoryNode.tips }} />
                            </div>
                          )}
                          {renderDiagramsByPlacement(selectedCategoryNode, 2)}
                          {renderDynamicComponentsByPlacement(selectedCategoryNode, 2)}
                          {selectedCategoryNode.example?.trim() && (
                            <div style={{ marginBottom: 16 }}>
                              <div style={{ padding: '8px 12px', backgroundColor: '#ecfdf5', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#065f46', marginBottom: 8 }}>Example</div>
                              <MathJax>
                                <div className="html-content" style={{ padding: '10px 14px', backgroundColor: '#f0fdf4', borderRadius: 6, fontSize: 13, lineHeight: 1.6 }}
                                  dangerouslySetInnerHTML={{ __html: convertLatexToHtml(selectedCategoryNode.example) }} />
                              </MathJax>
                            </div>
                          )}
                          {renderDiagramsByPlacement(selectedCategoryNode, 3)}
                          {renderDynamicComponentsByPlacement(selectedCategoryNode, 3)}
                          {renderDiagramsByPlacement(selectedCategoryNode, 4)}
                          {renderDynamicComponentsByPlacement(selectedCategoryNode, 4)}
                          {renderDiagramsByPlacement(selectedCategoryNode, 5)}
                          {renderDynamicComponentsByPlacement(selectedCategoryNode, 5)}
                        </div>
                      ) : !showCategoryHelp && (
                      <>
                      <div ref={questionHtmlRef} style={{ marginBottom: isMobile || isTablet ? 28 : 56, paddingTop: isMobile || isTablet ? 0 : 24, textAlign: 'left' }}>
                        {selectedQuestion.questionText && (
                          <div
                            className="html-content text-base sm:text-lg font-semibold text-slate-900 leading-relaxed"
                            style={{ marginBottom: selectedQuestion.questionLatex ? 12 : 0 }}
                            dangerouslySetInnerHTML={{ __html: selectedQuestion.questionText }}
                          />
                        )}
                        {selectedQuestion.questionLatex && (
                          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-slate-900">
                            <MathJax dynamic>{`$$${selectedQuestion.questionLatex}$$`}</MathJax>
                          </div>
                        )}
                      </div>

                      {selectedQuestion.imageUrl && (
                        <div style={{ marginBottom: 20, textAlign: 'left' }}>
                          <img
                            src={selectedQuestion.imageUrl}
                            alt="Question visual"
                            style={{
                              maxWidth: '100%',
                              width: isMobile ? '100%' : isTablet ? '100%' : 400,
                              height: 'auto',
                              objectFit: 'contain',
                              borderRadius: 6,
                              border: '1px solid #e0e0e0'
                            }}
                          />
                        </div>
                      )}

                      {/* Answer Input Box */}
                      <div style={{ marginBottom: 28, textAlign: 'left' }}>
                        <p style={{ fontSize: 12, color: '#999', marginBottom: 8, textTransform: 'uppercase', fontWeight: 600 }}>
                          Your Answer
                        </p>
                        {selectedQuestion.isMultipleChoice ? (
                          <div style={{
                            border: answerErrors[selectedQuestion.id] ? '2px solid #f44336' : '1px solid #e0e0e0',
                            borderRadius: 6,
                            padding: 10,
                            backgroundColor: '#fff'
                          }}>
                            {getQuestionOptionEntries(selectedQuestion).map(option => (
                              <label
                                key={option.key}
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 8,
                                  padding: '8px 4px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9'
                                }}
                              >
                                <input
                                  type="radio"
                                  name={`question-${selectedQuestion.id}-option`}
                                  checked={(userAnswers[selectedQuestion.id] || '') === (option.value || '')}
                                  onChange={() => {
                                    setUserAnswers({
                                      ...userAnswers,
                                      [selectedQuestion.id]: option.value || ''
                                    });
                                    const newErrors = { ...answerErrors };
                                    delete newErrors[selectedQuestion.id];
                                    setAnswerErrors(newErrors);
                                  }}
                                  style={{ marginTop: 2 }}
                                />
                                <span style={{ fontSize: isMobile ? 13 : 16, color: '#1f2937', lineHeight: 1.5 }}>
                                  <strong>{option.key}.</strong>{' '}
                                  <MathJax inline dynamic>
                                    <span dangerouslySetInnerHTML={{ __html: toHtmlMarkup(option.value) }} />
                                  </MathJax>
                                </span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <textarea
                            ref={answerInputRef}
                            value={userAnswers[selectedQuestion.id] || ''}
                            onChange={(e) => {
                              setUserAnswers({
                                ...userAnswers,
                                [selectedQuestion.id]: e.target.value
                              });
                              // Clear error when user starts typing
                              const newErrors = { ...answerErrors };
                              delete newErrors[selectedQuestion.id];
                              setAnswerErrors(newErrors);
                            }}
                            placeholder="Type your answer here..."
                            style={{
                              width: '100%',
                              minHeight: isMobile ? 80 : 100,
                              padding: isMobile ? 10 : 12,
                              fontSize: isMobile ? 13 : 14,
                              border: answerErrors[selectedQuestion.id] ? '2px solid #f44336' : '1px solid #e0e0e0',
                              borderRadius: 6,
                              fontFamily: 'inherit',
                              resize: 'vertical',
                              boxSizing: 'border-box'
                            }}
                          />
                        )}
                        
                        {/* Error Message */}
                        {answerErrors[selectedQuestion.id] && (
                          <p style={{
                            marginTop: 8,
                            padding: 8,
                            backgroundColor: '#ffebee',
                            color: '#c62828',
                            borderRadius: 4,
                            fontSize: 12,
                            fontWeight: 500
                          }}>
                            ⚠️ {answerErrors[selectedQuestion.id]}
                          </p>
                        )}
                        
                        {/* Button and Result Indicator Row */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 20,
                          marginTop: 12,
                          justifyContent: isSmallScreen ? 'flex-end' : 'space-between'
                        }}>
                          <button
                          onClick={async () => {
                            const userAnswer = (userAnswers[selectedQuestion.id] || '').trim();
                            
                            // Validation: Check if answer is empty
                            if (!userAnswer) {
                              setAnswerErrors({
                                ...answerErrors,
                                [selectedQuestion.id]: selectedQuestion.isMultipleChoice
                                  ? 'Please select an option before checking'
                                  : 'Please enter an answer before checking'
                              });
                              return;
                            }
                            
                            // Clear error if user had previously entered an invalid answer
                            const newErrors = { ...answerErrors };
                            delete newErrors[selectedQuestion.id];
                            setAnswerErrors(newErrors);
                            
                            const newSubmitted = new Set(submittedAnswers);
                            newSubmitted.add(selectedQuestion.id);
                            setSubmittedAnswers(newSubmitted);
                            console.log(selectedQuestion);
                            
                            // Check if answer is correct
                            const correctAnswer = getCorrectAnswerForQuestion(selectedQuestion);
                            const isCorrect = isAnswerCorrect(
                              userAnswer,
                              correctAnswer
                            );
                            setAnswerResults({
                              ...answerResults,
                              [selectedQuestion.id]: isCorrect
                            });

                            // Save attempt to backend
                            if (isAuthenticated) {
                              try {
                                const token = await getAccessTokenSilently();
                                await fetch(`${config.apiBaseUrl}/api/QuestionAttempts`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                  },
                                  body: JSON.stringify({
                                    questionId: selectedQuestion.id,
                                    userAnswer: userAnswer,
                                    isCorrect: isCorrect,
                                    categoryId: selectedCategory
                                  })
                                });
                                
                                // Update last attempt status in state
                                setLastAttemptStatus({
                                  ...lastAttemptStatus,
                                  [selectedQuestion.id]: {
                                    isCorrect: isCorrect,
                                    attemptedAt: new Date().toISOString()
                                  }
                                });
                                
                              } catch (error) {
                                console.error('Error saving question attempt:', error);
                                // Don't show error to user, just log it
                              }
                            }
                          }}
                          style={{
                            padding: '8px 20px',
                            backgroundColor: '#4f46e5',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 14,
                            fontWeight: 600,
                            transition: 'background-color 0.2s ease',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#4338ca';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = '#4f46e5';
                          }}
                        >
                          Check Answer
                        </button>

                        {/* Result Indicator - Same Row */}
                        {submittedAnswers.has(selectedQuestion.id) && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: 8,
                            borderRadius: 6,
                            backgroundColor: answerResults[selectedQuestion.id] ? '#e8f5e9' : '#ffebee',
                            border: '2px solid',
                            borderColor: answerResults[selectedQuestion.id] ? '#4caf50' : '#f44336'
                          }}>
                            <div style={{
                              fontSize: 20,
                              fontWeight: 'bold',
                              color: answerResults[selectedQuestion.id] ? '#4caf50' : '#f44336'
                            }}>
                              {answerResults[selectedQuestion.id] ? '✓' : '✗'}
                            </div>
                            <div style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: answerResults[selectedQuestion.id] ? '#2e7d32' : '#c62828'
                            }}>
                              {answerResults[selectedQuestion.id] ? 'Correct!' : 'Incorrect'}
                            </div>
                          </div>
                        )}
                      </div>
                      </div>

                      {/* Show Answer Only After Submission */}
                      {submittedAnswers.has(selectedQuestion.id) && (
                        <>
                          {!answerResults[selectedQuestion.id] && (
                            <p style={{
                              marginTop: 12,
                              fontSize: 12,
                              color: '#c62828',
                              backgroundColor: '#ffebee',
                              padding: 8,
                              borderRadius: 4
                            }}>
                              Please try again or view the correct answer below
                            </p>
                          )}

                          <div style={{ marginBottom: 20, textAlign: 'left', padding: 16, backgroundColor: '#e8f5e9', borderRadius: 6, border: '1px solid #c8e6c9', marginTop: 20 }}>
                            <p style={{ fontSize: 12, color: '#2e7d32', marginBottom: 4, textTransform: 'uppercase', fontWeight: 600 }}>
                              ✓ Correct Answer
                            </p>
                            {selectedQuestion.isMultipleChoice ? (
                              <p style={{ fontSize: 15, color: '#2e7d32', lineHeight: 1.6, fontWeight: 500 }}>
                                {getCorrectAnswerForQuestion(selectedQuestion)}
                              </p>
                            ) : selectedQuestion.answerLatex ? (
                              <div style={{ fontSize: 15, color: '#2e7d32', lineHeight: 1.6, fontWeight: 500 }}>
                                <MathJax>{`$$${selectedQuestion.answerLatex}$$`}</MathJax>
                              </div>
                            ) : (
                              <p style={{ fontSize: 15, color: '#2e7d32', lineHeight: 1.6, fontWeight: 500 }}>
                                {selectedQuestion.answerText}
                              </p>
                            )}
                          </div>

                        </>
                      )}

                      {/* Practice Drawing Section */}
                      <div style={{ marginBottom: 20, textAlign: 'left' }}>
                        <button
                          onClick={() => setIsDrawingPanelOpen(!isDrawingPanelOpen)}
                          style={{
                            padding: '10px 16px',
                            backgroundColor: '#ff9800',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 14,
                            fontWeight: 600,
                            marginBottom: 12,
                            transition: 'background-color 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#f57c00';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = '#ff9800';
                          }}
                        >
                          ✏️ {isDrawingPanelOpen ? 'Hide' : 'Show'} Practice Area
                        </button>

                        {isDrawingPanelOpen && (
                          <DrawingCanvas 
                            canvasRef={excalidrawRef}
                            onQuestionChange={() => {
                              // Reset canvas when question changes
                              if (excalidrawRef.current) {
                                const ctx = excalidrawRef.current.getContext('2d');
                                if (ctx) {
                                  ctx.fillStyle = '#ffffff';
                                  ctx.fillRect(0, 0, excalidrawRef.current.width, excalidrawRef.current.height);
                                  ctx.strokeStyle = '#e0e0e0';
                                  ctx.lineWidth = 1;
                                  ctx.strokeRect(0, 0, excalidrawRef.current.width, excalidrawRef.current.height);
                                }
                              }
                            }}
                          />
                        )}
                      </div>

                      {/* Post-submission explanation tools */}
                      {submittedAnswers.has(selectedQuestion.id) && (
                        <>

                          {selectedQuestion.explanation && (
                            <div style={{ marginBottom: 20, textAlign: 'left', padding: 16, backgroundColor: '#e3f2fd', borderRadius: 6, border: '1px solid #bbdefb' }}>
                              <p style={{ fontSize: 12, color: '#1565c0', marginBottom: 4, textTransform: 'uppercase', fontWeight: 600 }}>
                                Explanation
                              </p>
                              <p style={{ fontSize: 14, color: '#0d47a1', lineHeight: 1.6 }}>
                                {selectedQuestion.explanation}
                              </p>
                            </div>
                          )}

                          {/* AI Step-by-Step Explanation */}
                          <div style={{ marginBottom: 12, textAlign: 'left' }}>
                            <button
                              onClick={handleGetAiExplanation}
                              disabled={loadingExplanation}
                              style={{
                                padding: '10px 16px',
                                backgroundColor: loadingExplanation ? '#94a3b8' : '#6366f1',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 6,
                                cursor: loadingExplanation ? 'not-allowed' : 'pointer',
                                fontSize: 14,
                                fontWeight: 600,
                                transition: 'background-color 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                if (!loadingExplanation) {
                                  e.currentTarget.style.backgroundColor = '#4f46e5';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (!loadingExplanation) {
                                  e.currentTarget.style.backgroundColor = '#6366f1';
                                }
                              }}
                            >
                              {loadingExplanation ? 'Loading...' : '📚 Show Explanation'}
                            </button>
                          </div>

                          {loadingExplanation && (
                            <div style={{ marginBottom: 20, textAlign: 'left', padding: 12, backgroundColor: '#eef2ff', borderRadius: 6, border: '1px solid #c7d2fe', color: '#3730a3', fontSize: 13, fontWeight: 600 }}>
                              Generating step-by-step explanation...
                            </div>
                          )}

                          {explanationError && (
                            <div style={{ marginBottom: 20, textAlign: 'left', padding: 12, backgroundColor: '#ffebee', color: '#c62828', borderRadius: 6 }}>
                              {explanationError}
                            </div>
                          )}

                          {(aiExplanationJson || aiExplanation) && (
                            <div style={{ marginBottom: 20, textAlign: 'left', padding: 16, backgroundColor: '#eef2ff', borderRadius: 8, border: '1px solid #c7d2fe' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                                <p style={{ fontSize: 12, color: '#3730a3', margin: 0, textTransform: 'uppercase', fontWeight: 700 }}>
                                  Explanation
                                </p>
                                {aiExplanationSource && (
                                  <span style={{ fontSize: 11, color: '#4338ca', backgroundColor: '#e0e7ff', border: '1px solid #c7d2fe', borderRadius: 999, padding: '2px 8px', fontWeight: 600 }}>
                                    Source: {aiExplanationSource.toUpperCase()}
                                  </span>
                                )}
                              </div>

                              {aiExplanationJson ? (
                                <div style={{ fontSize: 14, color: '#333', lineHeight: 1.65, fontFamily: 'inherit' }}>
                                  {aiExplanationJson.title && (
                                    <p style={{ marginTop: 0, marginBottom: 10, fontWeight: 700, color: '#312e81' }}>{aiExplanationJson.title}</p>
                                  )}

                                  {Array.isArray(aiExplanationJson.steps) && aiExplanationJson.steps.length > 0 && (
                                    <div style={{ marginBottom: 12 }}>
                                      {aiExplanationJson.steps.map((step, index) => (
                                        <div key={`step-${index}`} style={{ marginBottom: 10 }}>
                                          <p style={{ margin: 0, fontWeight: 600, color: '#1f2937' }}>
                                            Step {step.stepNumber ?? index + 1}
                                          </p>
                                          {step.explanation && (
                                            <p style={{ margin: '4px 0 0 0' }}>
                                              <MathJax dynamic>{formatInlineLatexForMathJax(step.explanation)}</MathJax>
                                            </p>
                                          )}
                                          {step.equationLatex && (
                                            <MathJax dynamic>{`$$${step.equationLatex}$$`}</MathJax>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {aiExplanationJson.finalAnswer && (
                                    <p style={{ margin: '8px 0', fontWeight: 700, color: '#065f46' }}>
                                      <MathJax dynamic>{`Final Answer: ${formatInlineLatexForMathJax(aiExplanationJson.finalAnswer)}`}</MathJax>
                                    </p>
                                  )}

                                  {Array.isArray(aiExplanationJson.checks) && aiExplanationJson.checks.length > 0 && (
                                    <div style={{ marginTop: 8 }}>
                                      <p style={{ margin: '0 0 4px 0', fontWeight: 600, color: '#334155' }}>Checks</p>
                                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                                        {aiExplanationJson.checks.map((check, index) => (
                                          <li key={`check-${index}`}>
                                            <MathJax dynamic>{formatInlineLatexForMathJax(check)}</MathJax>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {Array.isArray(aiExplanationJson.tips) && aiExplanationJson.tips.length > 0 && (
                                    <MathJax>
                                      <div style={{ marginTop: 8 }}>
                                        <p style={{ margin: '0 0 4px 0', fontWeight: 600, color: '#334155' }}>Tips</p>
                                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                                          {aiExplanationJson.tips.map((tip, index) => (
                                            <li key={`tip-${index}`}>{tip}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    </MathJax>
                                  )}
                                </div>
                              ) : aiExplanation ? (
                                <MathJax>
                                  <div
                                    className="html-content"
                                    style={{ fontSize: 14, color: '#333', lineHeight: 1.6, fontFamily: 'inherit' }}
                                    dangerouslySetInnerHTML={{ __html: convertLatexToHtml(aiExplanation) }}
                                  />
                                </MathJax>
                              ) : null}
                            </div>
                          )}
                        </>
                      )}
                      </>
                      )}
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: 12,
                        justifyContent: 'space-between',
                        marginTop: 16,
                        paddingTop: 16,
                        borderTop: '1px solid #e5e7eb',
                        backgroundColor: '#fff',
                        flexShrink: 0,
                        ...(isSmallScreen ? {
                          position: 'fixed',
                          bottom: 72,
                          left: 0,
                          right: 0,
                          zIndex: 39,
                          padding: '10px 16px',
                          borderTop: '1px solid #e5e7eb',
                          marginTop: 0
                        } : {})
                      }}>
                        <button
                          onClick={() => {
                            const currentIndex = questions.findIndex(q => q.id === selectedQuestion.id);
                            if (currentIndex > 0) {
                              setSelectedQuestion(questions[currentIndex - 1]);
                            }
                          }}
                          disabled={questions.findIndex(q => q.id === selectedQuestion.id) === 0}
                          style={{
                            flex: 1,
                            padding: '10px 16px',
                            backgroundColor: questions.findIndex(q => q.id === selectedQuestion.id) === 0 ? '#cbd5e1' : '#334155',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            cursor: questions.findIndex(q => q.id === selectedQuestion.id) === 0 ? 'not-allowed' : 'pointer',
                            fontSize: 14,
                            fontWeight: 600,
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseOver={(e) => {
                            if (questions.findIndex(q => q.id === selectedQuestion.id) !== 0) {
                              e.currentTarget.style.backgroundColor = '#1e293b';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (questions.findIndex(q => q.id === selectedQuestion.id) !== 0) {
                              e.currentTarget.style.backgroundColor = '#334155';
                            }
                          }}
                        >
                          ← Previous
                        </button>
                        
                        <button
                          onClick={() => {
                            const currentIndex = questions.findIndex(q => q.id === selectedQuestion.id);
                            if (currentIndex < accessibleQuestionCount - 1) {
                              setSelectedQuestion(questions[currentIndex + 1]);
                            } else if (userSubscriptionTier < 999 && questions.length > accessibleQuestionCount) {
                              setAccessDeniedMessage(`🔒 This question is locked. Upgrade to access all ${questions.length} questions in this category.`);
                            }
                          }}
                          disabled={questions.findIndex(q => q.id === selectedQuestion.id) === accessibleQuestionCount - 1}
                          style={{
                            flex: 1,
                            padding: '10px 16px',
                            backgroundColor: questions.findIndex(q => q.id === selectedQuestion.id) === accessibleQuestionCount - 1 ? '#cbd5e1' : '#334155',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            cursor: questions.findIndex(q => q.id === selectedQuestion.id) === accessibleQuestionCount - 1 ? 'not-allowed' : 'pointer',
                            fontSize: 14,
                            fontWeight: 600,
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseOver={(e) => {
                            if (questions.findIndex(q => q.id === selectedQuestion.id) !== accessibleQuestionCount - 1) {
                              e.currentTarget.style.backgroundColor = '#1e293b';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (questions.findIndex(q => q.id === selectedQuestion.id) !== accessibleQuestionCount - 1) {
                              e.currentTarget.style.backgroundColor = '#334155';
                            }
                          }}
                        >
                          Next →
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        )}

      </div>

      {(showQuestionListBackBar || showLockedQuestionsBackBar) && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-2 py-4 min-h-[72px] z-40">
          <div className="flex flex-nowrap items-center justify-end gap-2 overflow-x-auto">
            <button
              onClick={handleBackFromQuestionList}
              className="shrink-0 whitespace-nowrap px-3 py-2 bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-lg text-sm font-semibold hover:bg-indigo-200 transition-colors"
            >
              Back
            </button>
          </div>
        </div>
      )}

      {showCategorySelectionBackBar && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-2 py-4 min-h-[72px] z-40">
          <div className="flex flex-nowrap items-center justify-end gap-2 overflow-x-auto">
            <button
              onClick={handleBackToCourseDetailsOrCourses}
              className="shrink-0 whitespace-nowrap inline-flex items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-100 px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition-colors hover:bg-indigo-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 6l-5 4 5 4" />
              </svg>
              Back to Course
            </button>
          </div>
        </div>
      )}

      {showQuestionDetailsBackBar && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-2 py-4 min-h-[72px] z-40">
          <div className="flex flex-nowrap items-center justify-end gap-2 overflow-x-auto">
            <button
              onClick={() => {
                setShowQuestionDetails(false);
                setSelectedQuestion(null);
              }}
              className="shrink-0 whitespace-nowrap px-3 py-2 bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-lg text-sm font-semibold hover:bg-indigo-200 transition-colors"
            >
              Back to Questions
            </button>
          </div>
        </div>
      )}
    </MathJaxContext>
  );
};

export default PracticeTasks;
